<template>
  <div>
    <div class="content">
      <div class="search-section">
        <div class="main-section-bottom">
          <div class="container">
            <!-- TABLEAU mode: single-row layout (back / centered query / actions) -->
            <div v-if="dossierMode === 'TABLEAU'" class="search-header search-header--tableau">
              <div class="search-header-left">
                <button class="button search-button back-button" @click="goDashboard">
                  <svg
                    xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                    class="bi bi-arrow-left-circle" viewBox="0 0 16 16"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8m15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-4.5-.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z"
                    />
                  </svg>
                  {{ $t('common.back') }}
                </button>
              </div>

              <div class="search-header-title">
                <p class="search-query">
                  <strong v-html="formatSearchQuery(dossierData && dossierData.contexte ? dossierData.contexte : $t('dossier.loading.searchQuery'))"></strong>
                </p>
              </div>

              <div class="search-header-right">
                <div class="actions-buttons">
                  <button
                    class="button search-button excel-button"
                    @click="exportTableauToExcel"
                    :disabled="!tableauData || tableauData.length === 0"
                    :title="$t('dossier.tooltips.exportExcel')"
                  >
                    {{ isExportingExcel ? $t('dossier.actions.exporting') : $t('dossier.actions.excel') }}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style="margin-left:5px;">
                      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                      <path d="M4.5 12.5A.5.5 0 0 1 5 12h1.5v-2H5a.5.5 0 0 1 0-1h1.5V7H5a.5.5 0 0 1 0-1h6a.5.5 0 0 1 0 1H9.5v2H11a.5.5 0 0 1 0 1H9.5v2H11a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5"/>
                    </svg>
                  </button>
                  <button class="button search-button" @click="goDashboard">
                    {{ $t('dossier.actions.newSearch') }}
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-plus-circle" viewBox="0 0 16 16" style="margin-left: 6px;">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                      <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Other modes (FLASH / AGENT / Normal): original two-row layout with collapsible query -->
            <div v-else class="search-header">
              <!-- Row 1: Retour (left) — Actions (right) -->
              <div class="search-header-row search-header-row--top">
                <div class="search-header-left">
                  <button style="display: flex; margin-bottom: 20px; align-self: flex-start" @click="goDashboard">
                    <svg
                      xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                      class="bi bi-arrow-left-circle" viewBox="0 0 16 16"
                      style="margin-right: 5px; margin-top: 4px"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8m15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-4.5-.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z"
                      />
                    </svg>
                    {{ $t('common.back') }}
                  </button>
                </div>

                <div class="search-header-right">
                  <div class="actions-buttons">
                    <button
                      class="button search-button memo-header-btn"
                      @click="openTransformModal('memo')"
                      :disabled="!isDataReadyForPDF"
                      :title="!isDataReadyForPDF ? $t('dossier.tooltips.waitAnalysis') : $t('dossier.tooltips.transformToMemo')"
                    >
                      {{ $t('dossier.actions.transformToMemo') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style="margin-left: 5px;">
                        <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z"/>
                        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                      </svg>
                    </button>

                    <div class="word-export-container">
                      <button
                        class="button search-button word-button"
                        @click="showWordExportModal = !showWordExportModal"
                        :disabled="isDownloadingWord || !isDataReadyForPDF"
                        :title="!isDataReadyForPDF ? $t('dossier.tooltips.waitAnalysis') : ''"
                      >
                        {{ isDownloadingWord ? $t('dossier.actions.downloading') : $t('dossier.actions.word') }}
                        <svg v-if="isDownloadingWord" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-repeat spinner" viewBox="0 0 16 16" style="margin-left: 5px;">
                          <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                          <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-file-earmark-word" viewBox="0 0 16 16" style="margin-left: 5px;">
                          <path d="M5.485 6.879a.5.5 0 1 0-.97.242l1.5 6a.5.5 0 0 0 .967.01L8 9.402l1.018 3.73a.5.5 0 0 0 .967-.01l1.5-6a.5.5 0 0 0-.97-.242l-1.036 4.144-.997-3.655a.5.5 0 0 0-.964 0l-.997 3.655L5.485 6.88z"/>
                          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                        </svg>
                      </button>

                      <div v-if="showWordExportModal" class="word-export-dropdown">
                        <button @click="downloadWord(true)" class="dropdown-option">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5"/>
                          </svg>
                          {{ $t('dossier.actions.shortAnswer') }}
                        </button>
                        <button @click="downloadWord(false)" class="dropdown-option">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/>
                          </svg>
                          {{ $t('dossier.actions.fullAnalysis') }}
                        </button>
                      </div>
                    </div>

                    <button class="button search-button" @click="goDashboard">
                      {{ $t('dossier.actions.newSearch') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-plus-circle" viewBox="0 0 16 16" style="margin-left: 6px;">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Row 2: centered, collapsible search query -->
              <div class="search-header-row search-header-row--bottom">
                <div class="search-query-container">
                  <p class="search-query" :class="{ 'collapsed-query': isSearchQueryCollapsible && !isSearchQueryExpanded }">
                    <strong v-html="formatSearchQuery(dossierData && dossierData.contexte ? dossierData.contexte : $t('dossier.loading.searchQuery'))"></strong>
                  </p>
                </div>
              </div>
              <div class="button-expand-row">
                <div class="button-expand-container">
                  <button v-if="isSearchQueryCollapsible" @click="toggleSearchQueryExpand" class="toggle-query-button">
                    <svg v-if="!isSearchQueryExpanded" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-down" viewBox="0 0 16 16">
                      <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-up" viewBox="0 0 16 16">
                      <path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div ref="progressBarSection" v-if="isAnalysing === 'pending'" class="search-section">
        <div class="main-section-bottom">
          <!-- Agent mode : timeline temps réel -->
          <div v-if="dossierMode === 'AGENT' && agentProgress && agentProgress.steps && agentProgress.steps.length > 0" class="agent-progress-container">
            <div class="agent-progress-header">
              <div class="agent-progress-title">
                <span class="agent-title-dot"></span>
                {{ $t('dossier.agent.analysing') }}
              </div>
              <div class="agent-progress-meta">{{ $t('dossier.agent.turn', { n: agentProgress.current_iteration || 1 }) }} · {{ elapsedTimeFormatted }}</div>
            </div>
            <div class="agent-timeline">
              <div v-for="(step, idx) in agentProgress.steps" :key="idx" class="agent-step" :class="['step-' + step.type, { 'step-loading': isStepLoading(idx) }]">
                <!-- Reasoning -->
                <template v-if="step.type === 'reasoning'">
                  <div class="step-icon-col"><div class="step-icon-bubble reasoning">R</div></div>
                  <div class="step-content-col">
                    <div class="step-label">{{ $t('dossier.agent.reasoning') }} <span class="step-iter">{{ $t('dossier.agent.turn', { n: step.iteration }) }}</span></div>
                    <div class="step-detail">{{ step.content }}</div>
                  </div>
                </template>
                <!-- Tool call: query_juriste -->
                <template v-if="step.type === 'tool_call' && step.tool === 'query_juriste'">
                  <div class="step-icon-col">
                    <div class="step-icon-bubble search" :class="{ 'icon-pulse': isStepLoading(idx) }">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                      </svg>
                    </div>
                  </div>
                  <div class="step-content-col">
                    <div class="step-label">
                      {{ $t('dossier.agent.legalSearch') }}
                      <span v-if="isStepLoading(idx)" class="step-loading-text-animated">{{ $t('dossier.agent.inProgress') }}</span>
                      <span class="step-iter">{{ $t('dossier.agent.turn', { n: step.iteration }) }}</span>
                    </div>
                    <div class="step-detail">{{ step.instruction }}</div>
                  </div>
                </template>
                <!-- Tool call: get_exact_doc -->
                <template v-if="step.type === 'tool_call' && step.tool === 'get_exact_doc'">
                  <div class="step-icon-col">
                    <div class="step-icon-bubble doc" :class="{ 'icon-pulse': isStepLoading(idx) }">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5z"/>
                        <path d="M14 4.5h-4A1.5 1.5 0 0 1 8.5 3V0z"/>
                      </svg>
                    </div>
                  </div>
                  <div class="step-content-col">
                    <div class="step-label">
                      {{ $t('dossier.agent.docConsultation') }}
                      <span v-if="isStepLoading(idx)" class="step-loading-text-animated">{{ $t('dossier.agent.inProgress') }}</span>
                      <span class="step-iter">{{ $t('dossier.agent.turn', { n: step.iteration }) }}</span>
                    </div>
                    <div class="step-detail">{{ step.reference }}</div>
                  </div>
                </template>
                <!-- Tool result: query_juriste -->
                <template v-if="step.type === 'tool_result' && step.tool === 'query_juriste'">
                  <div class="step-icon-col"><div class="step-icon-bubble success">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                    </svg>
                  </div></div>
                  <div class="step-content-col">
                    <div class="step-label step-label-success">{{ $t('dossier.agent.documentsFound', { n: step.nb_docs }) }}</div>
                    <div class="step-detail step-sources" v-if="step.sources_used && step.sources_used.length">{{ $t('dossier.agent.sourcesLabel', { sources: step.sources_used.join(', ') }) }}</div>
                  </div>
                </template>
                <!-- Tool result: get_exact_doc -->
                <template v-if="step.type === 'tool_result' && step.tool === 'get_exact_doc'">
                  <div class="step-icon-col"><div class="step-icon-bubble success">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                    </svg>
                  </div></div>
                  <div class="step-content-col">
                    <div class="step-label step-label-success">{{ step.title || step.source_id }}</div>
                    <div class="step-detail step-error" v-if="step.error">{{ step.error }}</div>
                  </div>
                </template>
                <!-- Answering -->
                <template v-if="step.type === 'answering'">
                  <div class="step-icon-col">
                    <div v-if="isAnalysing === 'pending'" class="step-icon-bubble writing icon-pulse">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207z"/>
                      </svg>
                    </div>
                    <div v-else class="step-icon-bubble success">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                      </svg>
                    </div>
                  </div>
                  <div class="step-content-col">
                    <div class="step-label">
                      <span v-if="isAnalysing === 'pending'" class="step-loading-text-animated">{{ $t('dossier.agent.writingAnswer') }}</span>
                      <span v-else>{{ $t('dossier.agent.answerWritten') }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <!-- Message de démarrage AGENT (pas encore de steps) -->
          <div v-else-if="dossierMode === 'AGENT'" class="agent-starting-message">
            <span class="agent-starting-dot"></span>
            {{ $t('dossier.agent.startingSearch') }}
          </div>
          <!-- Default progress bar (non-AGENT) -->
          <div v-else class="progress-bar-container">
            <div class="progress">
              <div
                class="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                :style="{ width: progressPercentage + '%' }"
                :aria-valuenow="progressPercentage"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {{ Math.round(progressPercentage) }}%
              </div>
            </div>
            <div class="analysis-message loading-message" style="margin-top: 10px;">
              <div class="title-points">
                <p><em>{{ analysisMessage }}</em></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section d'erreur quand l'analyse échoue -->
      <div v-if="isAnalysing === 'failed'" class="search-section">
        <div class="main-section-bottom">
          <div class="container">
            <div class="alert alert-danger" role="alert" style="margin-top: 20px; text-align: center; padding: 30px; border-radius: 8px;">
              <h4 class="alert-heading" style="color: #721c24; margin-bottom: 15px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-exclamation-triangle" viewBox="0 0 16 16" style="margin-right: 8px; vertical-align: text-bottom;">
                  <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
                  <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
                </svg>
                {{ $t('dossier.error.title') }}
              </h4>
              <p style="color: #721c24; font-size: 16px; margin-bottom: 10px;">{{ $t('dossier.error.analysisFailed') }}</p>
              <hr style="border-color: #f5c6cb;">
              <p class="mb-0" style="color: #721c24;">
                {{ $t('dossier.error.retryLater') }}
                <br>
                <button class="button search-button" style="margin-top: 20px;" @click="goDashboard">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-left-circle" viewBox="0 0 16 16" style="margin-right: 5px;">
                    <path fill-rule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8m15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-4.5-.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z"/>
                  </svg>
                  {{ $t('dossier.error.backToDashboard') }}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isAnalysing === 'done' && loading" class="search-section">
        <div class="main-section-bottom">
            <div class="progress-bar-container">
                <div class="analysis-message">
                    <div class="title-points" style="display: flex; align-items: center; justify-content: center; gap: 12px;">

                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-arrow-repeat spinner" viewBox="0 0 16 16">
                            <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                            <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                        </svg>

                        <p style="margin: 0;"><em>{{ $t('dossier.loading.results') }}</em></p>
                    </div>
                </div>
            </div>
        </div>
      </div>

      <!-- Agent timeline (persistent, collapsible) above the answer -->
      <div v-if="isAnalysing === 'done' && !loading && dossierMode === 'AGENT' && agentProgress && agentProgress.steps && agentProgress.steps.length > 0" class="search-section agent-done-timeline-section">
        <div class="agent-done-timeline">
          <div class="agent-done-header" @click="agentTimelineCollapsed = !agentTimelineCollapsed">
            <div class="agent-done-header-left">
              <svg v-if="agentTimelineCollapsed" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style="margin-right: 6px; flex-shrink: 0;">
                <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style="margin-right: 6px; flex-shrink: 0;">
                <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
              </svg>
              <span class="agent-done-title">{{ $t('dossier.agent.reasoningOfAgent') }}</span>
            </div>
            <div class="agent-done-meta">{{ $tc('dossier.agent.turns', agentProgress.current_iteration || 1, { n: agentProgress.current_iteration || 1 }) }} · {{ elapsedTimeFormatted }}</div>
          </div>
          <div v-if="!agentTimelineCollapsed" class="agent-timeline">
            <div v-for="(step, idx) in agentProgress.steps" :key="idx" class="agent-step" :class="['step-' + step.type]">
              <template v-if="step.type === 'reasoning'">
                <div class="step-icon-col"><div class="step-icon-bubble reasoning">R</div></div>
                <div class="step-content-col">
                  <div class="step-label">{{ $t('dossier.agent.reasoning') }} <span class="step-iter">{{ $t('dossier.agent.turn', { n: step.iteration }) }}</span></div>
                  <div class="step-detail">{{ step.content }}</div>
                </div>
              </template>
              <template v-if="step.type === 'tool_call' && step.tool === 'query_juriste'">
                <div class="step-icon-col">
                  <div class="step-icon-bubble search">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                    </svg>
                  </div>
                </div>
                <div class="step-content-col">
                  <div class="step-label">{{ $t('dossier.agent.legalSearch') }} <span class="step-iter">{{ $t('dossier.agent.turn', { n: step.iteration }) }}</span></div>
                  <div class="step-detail">{{ step.instruction }}</div>
                </div>
              </template>
              <template v-if="step.type === 'tool_call' && step.tool === 'get_exact_doc'">
                <div class="step-icon-col">
                  <div class="step-icon-bubble doc">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5z"/>
                      <path d="M14 4.5h-4A1.5 1.5 0 0 1 8.5 3V0z"/>
                    </svg>
                  </div>
                </div>
                <div class="step-content-col">
                  <div class="step-label">{{ $t('dossier.agent.docConsultation') }} <span class="step-iter">{{ $t('dossier.agent.turn', { n: step.iteration }) }}</span></div>
                  <div class="step-detail">{{ step.reference }}</div>
                </div>
              </template>
              <template v-if="step.type === 'tool_result' && step.tool === 'query_juriste'">
                <div class="step-icon-col"><div class="step-icon-bubble success">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                  </svg>
                </div></div>
                <div class="step-content-col">
                  <div class="step-label step-label-success">{{ $t('dossier.agent.documentsFound', { n: step.nb_docs }) }}</div>
                  <div class="step-detail step-sources" v-if="step.sources_used && step.sources_used.length">{{ $t('dossier.agent.sourcesLabel', { sources: step.sources_used.join(', ') }) }}</div>
                </div>
              </template>
              <template v-if="step.type === 'tool_result' && step.tool === 'get_exact_doc'">
                <div class="step-icon-col"><div class="step-icon-bubble success">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                  </svg>
                </div></div>
                <div class="step-content-col">
                  <div class="step-label step-label-success">{{ step.title || step.source_id }}</div>
                  <div class="step-detail step-error" v-if="step.error">{{ step.error }}</div>
                </div>
              </template>
              <template v-if="step.type === 'answering'">
                <div class="step-icon-col"><div class="step-icon-bubble success">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                  </svg>
                </div></div>
                <div class="step-content-col">
                  <div class="step-label">{{ $t('dossier.agent.answerWritten') }}</div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Coût LLM (mode Agent) — masqué côté user (debug admin via /cost-breakdown) -->
      <div v-if="false && isAnalysing === 'done' && !loading && dossierMode === 'AGENT' && dossierData && dossierData.total_cost_usd != null && dossierData.total_cost_usd > 0" class="search-section agent-cost-section">
        <div class="agent-cost-card">
          <div class="agent-cost-header" @click="toggleCostBreakdown">
            <div class="agent-cost-header-left">
              <svg v-if="costCollapsed" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style="margin-right: 6px; flex-shrink: 0;">
                <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style="margin-right: 6px; flex-shrink: 0;">
                <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
              </svg>
              <span class="agent-cost-title">Coût LLM</span>
            </div>
            <div class="agent-cost-meta">
              <strong>${{ Number(dossierData.total_cost_usd).toFixed(4) }}</strong>
              · {{ formatTokens(dossierData.total_tokens_in) }} in / {{ formatTokens(dossierData.total_tokens_out) }} out
            </div>
          </div>

          <div v-if="!costCollapsed" class="agent-cost-body">
            <div v-if="costLoading" class="agent-cost-loading">Chargement du détail…</div>
            <div v-else-if="costError" class="agent-cost-error">{{ costError }}</div>
            <template v-else-if="costBreakdown">
              <div class="agent-cost-grid">
                <div class="agent-cost-grid-section">
                  <h6>Par outil</h6>
                  <table class="agent-cost-table">
                    <tr v-for="(b, k) in costBreakdown.by_tool" :key="'t-' + k">
                      <td>{{ k }}</td>
                      <td class="agent-cost-num">{{ b.calls }}</td>
                      <td class="agent-cost-num">${{ b.cost_usd.toFixed(4) }}</td>
                    </tr>
                  </table>
                </div>
                <div class="agent-cost-grid-section">
                  <h6>Par étape</h6>
                  <table class="agent-cost-table">
                    <tr v-for="(b, k) in costBreakdown.by_step" :key="'s-' + k">
                      <td>{{ k }}</td>
                      <td class="agent-cost-num">{{ b.calls }}</td>
                      <td class="agent-cost-num">${{ b.cost_usd.toFixed(4) }}</td>
                    </tr>
                  </table>
                </div>
                <div class="agent-cost-grid-section">
                  <h6>Par modèle</h6>
                  <table class="agent-cost-table">
                    <tr v-for="(b, k) in costBreakdown.by_model" :key="'m-' + k">
                      <td>{{ k }}</td>
                      <td class="agent-cost-num">{{ b.calls }}</td>
                      <td class="agent-cost-num">${{ b.cost_usd.toFixed(4) }}</td>
                    </tr>
                  </table>
                </div>
                <div v-if="costBreakdown.by_source && Object.keys(costBreakdown.by_source).length" class="agent-cost-grid-section">
                  <h6>Par source</h6>
                  <table class="agent-cost-table">
                    <tr v-for="(b, k) in costBreakdown.by_source" :key="'src-' + k">
                      <td>{{ k }}</td>
                      <td class="agent-cost-num">{{ b.calls }}</td>
                      <td class="agent-cost-num">${{ b.cost_usd.toFixed(4) }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div ref="cadreJuridique" class="search-section" v-if="isAnalysing === 'done' && !loading && dossierMode !== 'TABLEAU'">
        <div class="section-carde">
          <div class="mes-epingles">
            <div class="section-content-container" :class="{ 'collapsed': !isExpanded && dossierMode !== 'FLASH' && dossierMode !== 'AGENT' }">
              <div class="section-header" style="display: flex; justify-content: center;">
                <h6 class="section-title">{{ $t('dossier.sections.shortAnswer') }}</h6>
              </div>
              <div class="section-content" id="section-content-content">
                <div
                  class="legal-text synthesis-content"
                  v-if="dossierData && dossierData.synthese"
                  v-html="formatSynthesisText(dossierData.synthese)"
                  @click.prevent="handleSynthesisReferenceClick"
                ></div>
                <hr>

                <!-- Boutons transformation (mémo / e-mail) -->
                <p class="transform-label">{{ $t('dossier.transform.label') }}</p>
                <div class="transform-buttons-row">
                  <button class="transform-action-btn memo-btn" @click="openTransformModal('memo')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z"/>
                        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                      </svg>
                      {{ $t('dossier.transform.memo') }}
                    </button>
                    <button class="transform-action-btn email-btn" @click="openTransformModal('email')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                      </svg>
                      {{ $t('dossier.transform.email') }}
                    </button>
                </div>

                <!-- Feedback utilisateur -->
                <FeedbackModal
                  :isVisible="isAnalysing === 'done'"
                  :dossierId="$route.params.dossierId"
                  :dossierMode="dossierMode"
                />

                <!-- Bouton Sources pour mode FLASH -->
                <div v-if="dossierMode === 'FLASH' || dossierMode === 'AGENT'" class="flash-sources-container">
                  <button class="button flash-sources-button" @click="openSourceModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-collection" viewBox="0 0 16 16">
                      <path d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1h-7zM0 13a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 16 13V6a1.5 1.5 0 0 0-1.5-1.5h-13A1.5 1.5 0 0 0 0 6v7zm1.5.5A.5.5 0 0 1 1 13V6a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5h-13z"/>
                    </svg>
                    {{ $t('dossier.sources.withCount', { n: countSources(analysesFiscales[0]) }) }}
                  </button>
                </div>

                <!-- Questions de suivi (gardées pour tous les modes) -->
                <div class="follow-up-questions">
                  <div class="custom-question-container">
                    <textarea
                      v-model="customQuestion"
                      :placeholder="$t('dossier.followUp.placeholder')"
                      class="follow-up-input"
                      rows="1"
                      @input="autoResize"
                      @keydown.enter.exact.prevent="handleCustomQuestion"
                    ></textarea>

                    <button
                      class="button follow-up-send-button"
                      :disabled="!customQuestion.trim()"
                      @click="handleCustomQuestion"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor"
                            class="bi bi-send-fill" viewBox="0 0 16 16">
                        <path d="M15.964 1.686a.5.5 0 0 0-.593-.592L1.12 6.524a.5.5 0 0 0 .02.938l4.547 1.515
                                  1.515 4.547a.5.5 0 0 0 .938.02l5.43-14.205z"/>
                      </svg>
                      {{ $t('common.send') }}
                    </button>
                  </div>
                  <button
                    v-if="dossierData.follow_up_question_1"
                    class="button follow-up-button"
                    @click="handleFollowUpQuestion(dossierData.follow_up_question_1)"
                  >
                    {{ dossierData.follow_up_question_1 }}
                  </button>
                  <button
                    v-if="dossierData.follow_up_question_2"
                    class="button follow-up-button"
                    @click="handleFollowUpQuestion(dossierData.follow_up_question_2)"
                  >
                    {{ dossierData.follow_up_question_2 }}
                  </button>
                </div>
                <hr>
              </div>
              <div class="fade-overlay" v-if="!isExpanded && dossierMode !== 'FLASH' && dossierMode !== 'AGENT'"></div>
            </div>
            <div class="show-more-container" v-if="dossierMode !== 'FLASH' && dossierMode !== 'AGENT'">
              <button class="button show-more-button" @click="toggleExpand">
                {{ isExpanded ? $t('dossier.actions.showLess') : $t('dossier.actions.showMore') }}
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi" :class="{ 'bi-chevron-down': !isExpanded, 'bi-chevron-up': isExpanded }" viewBox="0 0 16 16" style="margin-left: 6px;">
                  <path v-if="!isExpanded" fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                  <path v-else fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- RÉPONSE LONGUE SECTION - Cachée en mode FLASH et TABLEAU -->
      <div class="search-section" v-if="isAnalysing === 'done' && !loading && dossierMode !== 'FLASH' && dossierMode !== 'TABLEAU' && dossierMode !== 'AGENT'">
        <div class="main-section-bottom">
          <div class="researchs">
            <div class="section-header" style="display: flex; justify-content: center;">
              <h6 class="section-title">{{ $t('dossier.sections.longAnswer') }}</h6>
            </div>
            <div class="card-grid" v-if="loading">
              <div class="card-container">
                <div class="card">
                  <p class="card-text">{{ $t('dossier.loading.analyses') }}</p>
                </div>
              </div>
            </div>
            <div class="card-grid" v-else-if="error">
              <div class="card-container">
                <div class="card">
                  <p class="card-text">{{ error }}</p>
                </div>
              </div>
            </div>
            <div class="card-grid" v-else-if="dossierData && analysesFiscales && analysesFiscales.length > 0">
              <div class="card-container" v-for="(pointItem, index) in analysesFiscales" :key="index">
                <div class="card">
                  <p class="card-text">{{ pointItem.point }}</p>
                  <div class="source-count-badge">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-journal-text" viewBox="0 0 16 16">
                      <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
                      <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3z"/>
                    </svg>
                    {{ $tc('dossier.sources.sourceCount', countSources(pointItem), { n: countSources(pointItem) }) }}
                  </div>
                  <div class="card-actions">
                    <button class="button search-button" @click="handleAnalyseComplete(pointItem)">
                      {{ $t('dossier.actions.viewFullAnalysis') }}
                      <img src="@/assets/arrow-right-white.svg" :alt="$t('dossier.aria.arrowRight')" class="arrow-icon">
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-grid" v-else>
              <div class="card-container">
                <div class="card">
                  <p class="card-text">{{ $t('dossier.emptyState.noAnalysisPoint') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TABLEAU DE JURISPRUDENCE SECTION -->
      <div class="search-section tableau-section-full" v-if="isAnalysing === 'done' && !loading && dossierMode === 'TABLEAU'">
        <div class="main-section-bottom">
          <div v-if="tableauData && tableauData.length > 0" class="tableau-jurisprudence-wrapper">
            <div class="tableau-jurisprudence-scroll">
              <table class="tableau-jurisprudence">
                <thead>
                  <tr>
                    <th class="tableau-th-decision">{{ $t('dossier.tableau.decision') }}</th>
                    <th
                      v-for="(sq, idx) in tableauSubQuestions"
                      :key="'h-' + idx"
                      v-show="!hiddenColumns.includes(idx)"
                      class="tableau-th-column"
                      :title="sq"
                    >{{ sq }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, rowIdx) in tableauData"
                    :key="row.id_juritext || rowIdx"
                    v-show="!hiddenRows.includes(rowIdx)"
                  >
                    <td class="tableau-td-decision">
                      <div
                        class="tableau-cell-content tableau-cell-clickable tableau-decision-cell"
                        @click="openTableauDecisionPanel(row)"
                      >
                        <strong class="tableau-decision-title-link">{{ row.titre }}</strong>
                        <span v-if="row.date" class="tableau-decision-date">{{ row.date }}</span>
                      </div>
                    </td>
                    <td
                      v-for="(sq, idx) in tableauSubQuestions"
                      :key="'c-' + (row.id_juritext || rowIdx) + '-' + idx"
                      v-show="!hiddenColumns.includes(idx)"
                      class="tableau-td-column"
                    >
                      <div
                        class="tableau-cell-content tableau-cell-clickable"
                        @click="openTableauCellModal(sq, row.colonnes_json['Q' + (idx + 1)] || '—')"
                      >
                        <template v-if="getTableauCellBadge(row.colonnes_json['Q' + (idx + 1)])">
                          <span
                            class="tableau-cell-chip"
                            :class="'tableau-cell-chip--' + getTableauCellBadge(row.colonnes_json['Q' + (idx + 1)]).status"
                          >
                            {{ getTableauCellBadge(row.colonnes_json['Q' + (idx + 1)]).label }}
                          </span>
                          <span class="tableau-cell-text">{{ getTableauCellBadge(row.colonnes_json['Q' + (idx + 1)]).rest }}</span>
                        </template>
                        <template v-else>
                          <span class="tableau-cell-text">{{ row.colonnes_json['Q' + (idx + 1)] || '—' }}</span>
                        </template>
                      </div>
                    </td>   
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="tableau-pagination">
              <div class="tableau-pagination-summary">{{ $t('dossier.tableau.pagination.showing', { start: 1, end: 10, total: tableauData.length }) }}</div>
              <div class="tableau-pagination-controls">
                <button type="button" class="tableau-page-btn tableau-page-btn-action" disabled aria-label="Previous page">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L6.207 7.5l5.147 5.146a.5.5 0 0 1-.708.708l-5.5-5.5a.5.5 0 0 1 0-.708l5.5-5.5a.5.5 0 0 1 .708 0z"/>
                  </svg>
                </button>
                <div class="tableau-pagination-count-btn">
                <button type="button" class="tableau-page-btn tableau-page-btn--active" aria-current="page">1</button>
                <button type="button" class="tableau-page-btn">2</button>
                <button type="button" class="tableau-page-btn">3</button>
                </div>
                <button type="button" class="tableau-page-btn tableau-page-btn-action" aria-label="Next page">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l5.5 5.5a.5.5 0 0 1 0 .708l-5.5 5.5a.5.5 0 1 1-.708-.708L9.793 8 4.646 2.854a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div v-else class="tableau-empty">
            <p>{{ $t('dossier.tableau.noDecision') }}</p>
          </div>
        </div>
      </div>
    </div>

    <LegalAnalysisModal
      v-if="selectedPoint"
      :pointData="selectedPoint"
      :isVisible="showAnalysisModal"
      :initialDoc="initialDocToOpen"
      :isFlashMode="dossierMode === 'FLASH' || dossierMode === 'AGENT'"  @close="closeAnalysisModal"
    />

    <!-- Panneau de détails d'article (composant réutilisable) -->
    <ArticleDetailPanel
      :isVisible="showArticleDetailPanel"
      :articleData="articleDetailData"
      @close="closeArticleDetailPanel"
    />

    <!-- Preview d'une citation workspace [doc:N] -->
    <CitationPreviewModal
      v-if="showCitationModal && citationModalData"
      :citation="citationModalData"
      @close="showCitationModal = false"
    />

    <!-- Modal transformation (mémo / e-mail) -->
    <TransformModal
      :isVisible="showTransformModal"
      :mode="transformMode"
      :synthesisText="synthesisWithTitlesForTransform"
      :dossierId="$route.params.dossierId"
      @close="closeTransformModal"
    />

    <!-- Petit modal tableau : texte complet d'une cellule -->
    <div v-if="showTableauCellModal" class="tableau-cell-modal-overlay" @click.self="showTableauCellModal = false">
      <div class="tableau-cell-modal">
        <div class="tableau-cell-modal-header">
          <span class="tableau-cell-modal-title">{{ tableauCellModalTitle }}</span>
          <button class="tableau-cell-modal-close" @click="showTableauCellModal = false">×</button>
        </div>
        <div class="tableau-cell-modal-body">
          <pre class="tableau-cell-modal-text">{{ tableauCellModalText }}</pre>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { supabase } from "@/supabase/supabaseClient";
import { getCurrentUser } from "@/supabase/auth";
import LegalAnalysisModal from "./LegalAnalysisModal.vue";
import ArticleDetailPanel from "./ArticleDetailPanel.vue";
import TransformModal from "./TransformModal.vue";
import FeedbackModal from "./FeedbackModal.vue";
import CitationPreviewModal from "./CitationPreviewModal.vue";
import PdfGenerator from '@/services/PdfGenerator';
import TextFormatter from '@/services/TextFormatter';
import { buildArticleDetailData, getOfficialDecisionLink } from '@/utils/documentFormatters';
import DOMPurify from 'dompurify';
import html2pdf from 'html2pdf.js';
// import HTMLtoDOCX from 'html-to-docx';
// At the top of your <script> tag, add the imports from the new library
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ExternalHyperlink
} from 'docx';
import { saveAs } from 'file-saver'; // A helper to easily save the generated blob

// Canonical list of all reference type patterns — single source of truth
const REF_TYPES = 'Jurisprudence|Legiarti|Bofip|BOFiP|BOFIP|EuritextITA|EurLexITA|Euritext|CNN|BALO|Statuts|Convbi|Infofi|Infofin|Reglements|Règlement|Règlements|Reglement|BODACC|Energy|ActesSocietes|Concurrence|INPI|Mention|CNIL|BOSS|ACPR|AMF|CompCase|UPC|EDPB|AGCM|GiustiziaAmm';

export default {
  name: 'DossierPage',
  components: {
    LegalAnalysisModal,
    ArticleDetailPanel,
    TransformModal,
    FeedbackModal,
    CitationPreviewModal,
  },
  data() {
    return {
      dossierMode: null,
      isSearchQueryCollapsible: false,
      isSearchQueryExpanded: false,
      resizeObserver: null,
      isExpanded: false,
      dossierData: null,
      dossierName: "",
      customQuestion: '', // Pour la question personnalisée
      loading: true,
      error: null,
      showTextModal: false,
      modalTitle: "",
      modalText: "",
      analysesFiscales: [],
      showAnalysisModal: false,
      selectedPoint: null,
      initialDocToOpen: null,
      // Added properties from old page for progress bar and loading message
      isAnalysing: null, // 'pending', 'done', or null
      progressPercentage: 0,
      analysisMessage: "",
      progressInterval: null,
      messageInterval: null,
      isDownloadingPDF: false,
      isDownloadingWord: false,
      showWordExportModal: false,
      isExportingExcel: false,
      randomDocs: [
        this.getRandomInt(85, 125),   // First range: 85-125
        this.getRandomInt(185, 225),  // Second range: 185-225
        this.getRandomInt(385, 425),  // Third range: 385-425
        this.getRandomInt(745, 800),  // Fourth range: 745-800
      ],
      subscription: null,
      pollingInterval: null,
      pollingFrequency: 3000, // 5 seconds
      agentProgress: null, // { status, current_iteration, steps[] }
      agentTimelineCollapsed: true,
      // Coût LLM (mode Agent) — chargé à la demande via /copilot/dossier/<id>/usage
      costCollapsed: true,
      costLoading: false,
      costError: null,
      costBreakdown: null, // { by_tool, by_step, by_model, by_source }
      // Workspace citations (agent mode avec workspace sélectionné)
      workspaceCitationsMap: {}, // { "42": { title, page_start, page_end, scaleway_key, source_file } }
      showCitationModal: false,
      citationModalData: null,
      // Article detail panel properties
      showArticleDetailPanel: false,
      articleDetailData: {},
      // Transform modal (mémo / e-mail)
      showTransformModal: false,
      transformMode: 'memo',
      tableauData: null,
      tableauSubQuestions: [],
      hiddenColumns: [],
      hiddenRows: [],
      showTableauCellModal: false,
      tableauCellModalTitle: '',
      tableauCellModalText: '',
    };
  },

  computed: {
    isDataReadyForPDF() {
      // Check if we have all required data and analysis is complete
      return (
        this.dossierData &&
        this.analysesFiscales &&
        this.analysesFiscales.length > 0 &&
        this.isAnalysing === 'done' &&
        !this.loading
      );
    },

    /** Temps écoulé depuis le début de l'analyse agent, formaté en mm:ss */
    elapsedTimeFormatted() {
      if (!this.agentProgress || !this.agentProgress.steps || this.agentProgress.steps.length === 0) return '0:00';
      const firstTs = new Date(this.agentProgress.steps[0].timestamp).getTime();
      const lastTs = new Date(this.agentProgress.steps[this.agentProgress.steps.length - 1].timestamp).getTime();
      const elapsed = Math.max(0, Math.floor((lastTs - firstTs) / 1000));
      const min = Math.floor(elapsed / 60);
      const sec = elapsed % 60;
      return `${min}:${sec.toString().padStart(2, '0')}`;
    },

    /** Synthèse avec références remplacées par les titres (comme pour l'export Word) */
    synthesisWithTitlesForTransform() {
      if (!this.dossierData?.synthese) return '';
      return this.replaceReferencesWithTitlesForExport(this.dossierData.synthese);
    },
  },

  watch: {
    isAnalysing: {
      handler(newValue) {
        if (newValue === "pending") {
          this.startProgressBar();
          this.startMessageInterval();

          // When analysis is pending, scroll to progress bar
          if (this.dossierMode !== 'FLASH') {
            this.$nextTick(() => {
              this.scrollToProgressBar();
            });
          }
        } else {
          this.quickCompleteAndStopProgressBar();
          this.stopMessageInterval();

          // When analysis is complete, wait for DOM update then scroll
          if (newValue === "done") {
            this.$nextTick(() => {
              this.scrollToCadreJuridique();
            });
          }
        }
      },
      immediate: true,
    },

    'dossierData.contexte'() {
      this.checkQueryHeight();
    },
  },

  beforeDestroy() {
    // Clean up all intervals when component is destroyed
    this.stopProgressBar();
    this.stopMessageInterval();
    this.stopPolling(); // Clean up the polling interval

    window.removeEventListener('popstate', this.handlePopStateForModal);
    document.removeEventListener('click', this.handleClickOutsideDropdown);

    // Clean up Supabase subscription if it exists (for backward compatibility)
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },

  async mounted() {
    await this.fetchDossierData();
    await this.checkAnalysisStatus();

    this.checkQueryHeight();

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkQueryHeight();
      });
      this.$nextTick(() => {
        const queryElement = this.$el.querySelector('.search-query');
        if (queryElement) {
          this.resizeObserver.observe(queryElement);
        }
      });
    }

    window.addEventListener('popstate', this.handlePopStateForModal);
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutsideDropdown);
    if (this.isAnalysing === "pending") {
      this.startProgressBar();
      this.startMessageInterval();
      this.startPolling(); // Start polling if analysis is pending

      // Scroll to progress bar when analysis is pending
      this.$nextTick(() => {
        this.scrollToProgressBar();
      });
    } else if (this.isAnalysing === "done") {
      // If analysis is already complete, scroll to the cadre juridique
      if (this.dossierMode !== 'FLASH') {
        this.$nextTick(() => {
          this.scrollToCadreJuridique();
        });
      }
    }
    // this.listenToAnalysisStatus();
  },

  methods: {

    handlePopStateForModal() {
      // Si la modale est affichée mais que le hash #analysis a disparu de l'URL,
      // cela signifie que l'utilisateur a cliqué sur "précédent".
      if (this.showAnalysisModal && window.location.hash !== '#analysis') {
        this.closeAnalysisModal();
      }
    },

    handleClickOutsideDropdown(event) {
      // Fermer le dropdown si on clique en dehors
      const dropdown = this.$el.querySelector('.word-export-dropdown');
      const button = this.$el.querySelector('.word-export-container .word-button');

      if (this.showWordExportModal && dropdown && !dropdown.contains(event.target) && !button.contains(event.target)) {
        this.showWordExportModal = false;
      }
    },

    parseMarkdownTable(text) {
      if (!text) return text;

      // Regex pour détecter les tableaux markdown
      const tableRegex = /(\|[^\n]+\|\n)(\|[\s:]*-+[\s:]*\|[^\n]*\|\n)((?:\|[^\n]+\|\n?)+)/g;

      return text.replace(tableRegex, (match, headerRow, separatorRow, bodyRows) => {
        // Parse le header
        const headers = headerRow.trim().split('|').filter(cell => cell.trim());

        // Parse l'alignement depuis le separator
        const alignments = separatorRow.trim().split('|').filter(cell => cell.trim()).map(cell => {
          if (cell.startsWith(':') && cell.endsWith(':')) return 'center';
          if (cell.endsWith(':')) return 'right';
          return 'left';
        });

        // Parse les rows du body
        const rows = bodyRows.trim().split('\n').map(row =>
          row.trim().split('|').filter(cell => cell.trim())
        );

        // Construire le HTML du tableau
        let tableHtml = '<div class="table-wrapper"><table class="markdown-table">';

        // Header
        tableHtml += '<thead><tr>';
        headers.forEach((header, index) => {
          const alignment = alignments[index] || 'left';
          tableHtml += `<th style="text-align: ${alignment}">${this.processTableCellContent(header)}</th>`;
        });
        tableHtml += '</tr></thead>';

        // Body
        tableHtml += '<tbody>';
        rows.forEach(row => {
          if (row.length > 0) {
            tableHtml += '<tr>';
            row.forEach((cell, index) => {
              const alignment = alignments[index] || 'left';
              tableHtml += `<td style="text-align: ${alignment}">${this.processTableCellContent(cell)}</td>`;
            });
            tableHtml += '</tr>';
          }
        });
        tableHtml += '</tbody>';

        tableHtml += '</table></div>';
        return tableHtml;
      });
    },

    // Méthode pour traiter le contenu des cellules du tableau
    processTableCellContent(content) {
      if (!content) return '';

      let processed = content.trim();

      // Remplacer les <br> dans les cellules par des retours à la ligne HTML
      processed = processed.replace(/<br>/g, '<br/>');

      // Gérer le texte en gras
      processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Gérer l'italique
      processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');

      return processed;
    },

    // ─── Cost breakdown (mode Agent) ───
    formatTokens(n) {
      const v = Number(n || 0);
      if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
      if (v >= 1_000) return (v / 1_000).toFixed(1) + 'k';
      return String(v);
    },
    async toggleCostBreakdown() {
      this.costCollapsed = !this.costCollapsed;
      if (this.costCollapsed || this.costBreakdown || this.costLoading) return;
      this.costLoading = true;
      this.costError = null;
      try {
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;
        if (!token) {
          this.costError = 'Session expirée';
          return;
        }
        const dossierId = this.dossierData?.id;
        if (!dossierId) {
          this.costError = 'Dossier introuvable';
          return;
        }
        const resp = await axios.get(
          `${process.env.VUE_APP_API_URL}/copilot/dossier/${dossierId}/usage`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.costBreakdown = resp.data;
      } catch (e) {
        this.costError = e?.response?.data?.error || e.message || 'Erreur de chargement';
      } finally {
        this.costLoading = false;
      }
    },

    // Modifiez la méthode formatSynthesisText existante :
    isStepLoading(idx) {
      if (this.isAnalysing !== 'pending') return false;
      const steps = this.agentProgress?.steps || [];
      const step = steps[idx];
      if (!step) return false;
      if (step.type === 'tool_call') {
        // In progress if no tool_result exists after this index
        for (let j = idx + 1; j < steps.length; j++) {
          if (steps[j].type === 'tool_result') return false;
        }
        return true;
      }
      if (step.type === 'answering') return true;
      return false;
    },

    formatSynthesisText(text) {
      if (!text) return '';

      // Mode AGENT : la synthese est un JSON, on extrait le champ "answer"
      let contentToFormat = text;
      if (this.dossierMode === 'AGENT' && typeof text === 'string' && text.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(text);
          contentToFormat = parsed.answer || text;
        } catch (e) {
          contentToFormat = text;
        }
      }

      // D'abord, parser les tableaux markdown
      let formattedText = this.parseMarkdownTable(contentToFormat);

      // Ensuite, appliquer le formatage de base
      formattedText = this.formatText(formattedText);

      // Traiter les références (tous les modes : FLASH, AGENT, standard)
      formattedText = this.processSynthesisReferencesInText(formattedText);

      // Transformer les citations workspace [doc:N] en chips cliquables
      formattedText = this.injectWorkspaceCitations(formattedText);

      return DOMPurify.sanitize(formattedText, {
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 'br', 'li', 'ul', 'ol', 'p', 'div', 'span', 'a', 'sup', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
        ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel', 'data-ref-id', 'data-point-index', 'data-doc-index', 'data-source-key', 'data-workspace-node-id', 'data-ws-tip', 'title'],
      });
    },

    // Numéro stable par passage cité (ordre de première apparition) — le même
    // node_id garde son numéro sur tous les rendus et les tours suivants.
    wsCitationNumber(id) {
      if (!this._wsCiteNums) this._wsCiteNums = {};
      if (!(id in this._wsCiteNums)) {
        this._wsCiteNums[id] = Object.keys(this._wsCiteNums).length + 1;
      }
      return this._wsCiteNums[id];
    },

    injectWorkspaceCitations(text) {
      if (!text) return text;
      const map = this.workspaceCitationsMap || {};
      const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      return text.replace(/\[doc:(\d+)\]/g, (match, id) => {
        const meta = map[String(id)] || {};
        const full = meta.source_file || '';
        // Marqueur sans fichier derrière (racine/dossier cité, ID inconnu) :
        // rien à prévisualiser → on retire proprement le marqueur du texte.
        if (!full) return '';
        const fname = full.split('/').pop();
        let pages = '';
        if (meta.page_start != null) {
          pages = (meta.page_end != null && meta.page_end !== meta.page_start)
            ? ` p.${meta.page_start}-${meta.page_end}`
            : ` p.${meta.page_start}`;
        }
        const n = this.wsCitationNumber(String(id));
        return `<a href="#" class="workspace-citation" data-workspace-node-id="${id}" data-ws-tip="${esc(fname + pages)}">[${n}]</a>`;
      });
    },

    // Process references in synthesis text and replace with document titles
    processSynthesisReferencesInText(text) {
      if (!text || !this.analysesFiscales) return text;

      const expandedText = this.expandMultiReferences(text);

      if (this.dossierMode === 'FLASH' || this.dossierMode === 'AGENT') {
        // --- CAS 1: Mode FLASH / AGENT (single point, simple IDs) ---
        // La synthèse (qui est le final_answer) contient des ID simples (ex: "Legiarti - 1", "[BOFIP - 1]")
        // Le LLM peut produire "BOFIP" en majuscules et/ou avec des crochets

        const refRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\s*-\\s*\\d+(?:\\s*-\\s*\\d+)?\\]?)`, 'g');

        return expandedText.replace(refRegex, (match) => {
          const refMatch = match.match(new RegExp(`^\\[?(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)(?:\\s*-\\s*\\d+)?\\]?$`));

          if (!refMatch) return match;

          const refType = refMatch[1];

          const sourceKey = this.mapRefTypeToSourceKey(refType);
          if (!sourceKey) return `<span class="inactive-reference">${match}</span>`;

          if (this.analysesFiscales.length === 0) return `<span class="inactive-reference">${match}</span>`;

          // Cherche le doc par id_answer à travers tous les turns (fix multi-turn agent)
          const idAnswer = `${refType} - ${parseInt(refMatch[2], 10)}`;
          let document = null;
          let pointIndex = 0;
          let localDocIndex = parseInt(refMatch[2], 10) - 1; // fallback FLASH / turn unique
          for (let pi = 0; pi < this.analysesFiscales.length; pi++) {
            const pt = this.analysesFiscales[pi];
            const sourceBlock = pt.analyses?.find(b => b.source === sourceKey);
            if (!sourceBlock?.analysis) continue;
            const di = sourceBlock.analysis.findIndex(d => d.id_answer === idAnswer);
            if (di !== -1) {
              document = sourceBlock.analysis[di];
              pointIndex = pi;
              localDocIndex = di;
              break;
            }
          }

          if (document) {
              const documentTitle = this.getDocumentTitleForSynthesis(document, sourceKey, localDocIndex);
              const officialLink = document.official_link;
              let linkIconHtml = '';
              if (officialLink) {
                linkIconHtml = ` <a href="${officialLink}" target="_blank" rel="noopener noreferrer" class="official-link-icon" title="${this.$t('dossier.tooltips.viewOfficial')}"></a>`;
              }
              return `<a href="#" class="synthesis-reference" data-source="${sourceKey}" data-index="${localDocIndex}" data-point-index="${pointIndex}">${documentTitle}</a>${linkIconHtml}`;
          }

          return `<span class="inactive-reference">${match}</span>`;
        });

      } else {

        // AJOUT d'Actes dans la regex
        const refRegex = new RegExp(`((?:ID:\\s+)?(?:${REF_TYPES})\\s*-\\s*\\d+(?:\\s*-\\s*\\d+)?)`, 'g');

        return expandedText.replace(refRegex, (match) => {
          let refMatch = match.match(new RegExp(`^(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)\\s*-\\s*(\\d+)$`));

          let refType, refIndex, pointIndex;

          if (refMatch) {
            // NEW FORMAT: "Jurisprudence - 1 - 3"
            refType = refMatch[1];
            refIndex = parseInt(refMatch[2], 10) - 1;
            pointIndex = parseInt(refMatch[3], 10) - 1;
          } else {
            // Try to match the old format (without point index)
            refMatch = match.match(new RegExp(`^(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)$`));

            if (refMatch) {
              // OLD FORMAT: "Jurisprudence - 1"
              refType = refMatch[1];
              refIndex = parseInt(refMatch[2], 10) - 1;
              pointIndex = null;
            } else {
              return match;
            }
          }

          // Map reference types to source keys
          let sourceKey = null;
          if (refType === 'Jurisprudence') sourceKey = 'juritext';
          else if (refType === 'Legiarti') sourceKey = 'legiarti';
          else if (refType === 'Bofip' || refType === 'BOFiP') sourceKey = 'bofip';
          else if (refType === 'Euritext') sourceKey = 'euritext';
          else if (refType === 'CNN') sourceKey = 'cnn';
          else if (refType === 'BALO') sourceKey = 'balo';
          else if (refType === 'Statuts') sourceKey = 'statuts';
          else if (refType === 'Convbi') sourceKey = 'convbi';
          else if (refType === 'Infofi' || refType === 'Infofin') sourceKey = 'infofi';
          else if (refType === 'ActesSocietes') sourceKey = 'actes_societes';
          else if (refType === 'Reglements') sourceKey = 'reglements';
          else if (refType === 'Reglement') sourceKey = 'reglements';
          else if (refType === 'Règlements') sourceKey = 'reglements';
          else if (refType === 'Règlement') sourceKey = 'reglements';
          else if (refType === 'BODACC') sourceKey = 'bodacc';
          else if (refType === 'Energy') sourceKey = 'energy';
          else if (refType === 'Concurrence') sourceKey = 'concurrence';
        else if (refType === 'INPI') sourceKey = 'inpi';
        else if (refType === 'Mention') sourceKey = 'mention';
        else if (refType === 'CNIL') sourceKey = 'cnil';
        else if (refType === 'BOSS') sourceKey = 'boss';
        else if (refType === 'ACPR') sourceKey = 'acpr';
        else if (refType === 'AMF') sourceKey = 'amf';
        else if (refType === 'CompCase') sourceKey = 'eccomp';
        else if (refType === 'UPC') sourceKey = 'upc';
        else if (refType === 'EDPB') sourceKey = 'edpb';
        else if (refType === 'AGCM') sourceKey = 'agcm';
        else if (refType === 'GiustiziaAmm') sourceKey = 'giustizia_amministrativa';
        else if (refType === 'EuritextITA') sourceKey = 'jurisprudence_europe_italy';
        else if (refType === 'EurLexITA') sourceKey = 'eurlex_italy';

        if (!sourceKey) return `<span class="inactive-reference">${match}</span>`;

        let finalPointIndex = pointIndex;
          let document = null;

          if (pointIndex !== null) {
            // NEW FORMAT: Use provided point index
            if (pointIndex < this.analysesFiscales.length) {
              const point = this.analysesFiscales[pointIndex];
              document = this.getDocumentFromPoint(point, sourceKey, refIndex);
              finalPointIndex = pointIndex;
            }
          } else {
            // OLD FORMAT: Search for the document across all points
            const pointWithReference = this.findPointContainingReference(sourceKey, refIndex);
            if (pointWithReference) {
              document = this.getDocumentFromPoint(pointWithReference.point, sourceKey, refIndex);
              finalPointIndex = pointWithReference.pointIndex;
            }
          }

          if (document && finalPointIndex !== null) {
            // 🎯 Replace with document title + point index (1-based)
            const documentTitle = this.getDocumentTitleForSynthesis(document, sourceKey, refIndex);
            const enhancedRefText = `${documentTitle}`;
            const officialLink = document.official_link;
            let linkIconHtml = '';
            if (officialLink) {
              linkIconHtml = ` <a href="${officialLink}"></a>`;
            }
            return `<a href="#" class="synthesis-reference" data-source="${sourceKey}" data-index="${refIndex}" data-point-index="${finalPointIndex}">${enhancedRefText}</a>${linkIconHtml}`;
          }

          return `<span class="inactive-reference">${match}</span>`;
        });
      }
    },

    // ALSO UPDATE your expandMultiReferences method to handle both formats:
    expandMultiReferences(text) {
      if (!text) return '';

      // AJOUT d'Actes dans les regex
      const newMultiRefRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\]?)(\\s*(?:-\\s*|\\s+))(\\d+\\s*-\\s*\\d+(?:\\s*,\\s*\\d+\\s*-\\s*\\d+)*)`, 'g');

      const oldMultiRefRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\]?)(\\s*(?:-\\s*|\\s+))(\\d+(?:\\s*,\\s*\\d+)*)`, 'g');

      // First handle new format
      let result = text.replace(newMultiRefRegex, (match, refType, sep, numbersPart) => {
        const references = numbersPart.split(/\s*,\s*/);
        return references.map(ref => `${refType} - ${ref}`).join(', ');
      });

      // Then handle old format (on the result from above)
      result = result.replace(oldMultiRefRegex, (match, refType, sep, numbersPart) => {
        // Only process if this doesn't already look like it has been processed
        if (match.includes(' - ') && /\d+\s*-\s*\d+/.test(match)) {
          return match; // Already processed, skip
        }

        const numbers = numbersPart.split(/\s*,\s*/);
        return numbers.map(num => `${refType} - ${num}`).join(', ');
      });

      return result;
    },

    // Find which analysis point contains a specific reference
    findPointContainingReference(sourceKey, docIndex) {
      for (let pointIndex = 0; pointIndex < this.analysesFiscales.length; pointIndex++) {
        const point = this.analysesFiscales[pointIndex];

        // --- CORRECTION : Gestion spécifique pour Mention ---
        if (sourceKey === 'mention') {
          if (point.mentioned_docs && point.mentioned_docs[docIndex]) {
            return { pointIndex, point };
          }
          // Si c'est 'mention', on ne regarde pas dans point.analyses, on passe au point suivant
          continue;
        }
        // ----------------------------------------------------

        if (!point.analyses) continue;

        const sourceBlock = point.analyses.find(block => block.source === sourceKey);
        if (sourceBlock && sourceBlock.analysis && sourceBlock.analysis[docIndex]) {
          return { pointIndex, point };
        }
      }
      return null;
    },

    // Get document from point analysis
    getDocumentFromPoint(point, sourceKey, docIndex) {
      // --- CORRECTION : Gestion spécifique pour Mention ---
      if (sourceKey === 'mention') {
        if (point.mentioned_docs && point.mentioned_docs[docIndex]) {
          return point.mentioned_docs[docIndex];
        }
        return null;
      }
      // ----------------------------------------------------

      if (!point.analyses) return null;

      const sourceBlock = point.analyses.find(block => block.source === sourceKey);
      if (sourceBlock && sourceBlock.analysis && sourceBlock.analysis[docIndex]) {
        return sourceBlock.analysis[docIndex];
      }
      return null;
    },

    // Get document title for synthesis display - THIS IS THE KEY METHOD
    getDocumentTitleForSynthesis(document, sourceKey, index) {
      if (!document) return this.$t('dossier.docTitles.document', { n: index + 1 });

      // Handle BOFIP identifiant priority
      if (sourceKey === 'bofip' && document.identifiant) {
        return document.identifiant;
      }

      // Handle Statuts title from pdf_name
      if (sourceKey === 'statuts' && document.pdf_name) {
        return this.getStatutsTitleForSynthesis(document.pdf_name);
      }

      if (sourceKey === 'reglements') {
        if (document.title) {
          return document.title;
        }
        return this.$t('dossier.docTitles.reglement', { n: index + 1 });
      }

      // Handle Infofi with company name and operation title
      if (sourceKey === 'infofi') {
        let title = document.title || '';
        if (document.nom_societe && document['Titre opération']) {
          title = `${document.nom_societe} - ${document['Titre opération']}`;
        } else if (document.nom_societe) {
          title = document.nom_societe;
        } else if (document['Titre opération']) {
          title = document['Titre opération'];
        }
        return title || this.$t('dossier.docTitles.information', { n: index + 1 });
      }

      if (sourceKey === 'actes_societes') {
        if (document.siren && document.date_acte) {
          return this.$t('dossier.docTitles.acteSocietePrefix', { siren: document.siren, date: this.formatActesSocietesDate(document.date_acte) });
        } else if (document.title) {
          return document.title;
        }
        return this.$t('dossier.docTitles.acteSociete', { n: index + 1 });
      }

      if (sourceKey === 'concurrence') {
        if (document.numero_affaire && document.date_decision) {
          return this.$t('dossier.docTitles.concurrencePrefix', { date: document.date_decision, numero: document.numero_affaire });
        } else if (document.titre) {
          return document.titre;
        }
        return this.$t('dossier.docTitles.concurrence', { n: index + 1 });
      }

      if (sourceKey === 'inpi') {
        if (document.reference && document.date) {
          return `INPI - ${document.date} - ${document.reference}`;
        } else if (document.parties) {
          return document.parties;
        }
        return this.$t('dossier.docTitles.inpi', { n: index + 1 });
      }

      if (sourceKey === 'cnil') {
        return document.title || document.titre_complet || this.$t('dossier.docTitles.cnil', { n: index + 1 });
      }

      if (sourceKey === 'boss') {
        return document.title || this.$t('dossier.docTitles.boss', { n: index + 1 });
      }

      if (sourceKey === 'acpr') {
        return document.title || document.numero || this.$t('dossier.docTitles.acpr', { n: index + 1 });
      }

      if (sourceKey === 'amf') {
        return document.title || document.reference || this.$t('dossier.docTitles.amf', { n: index + 1 });
      }

      if (sourceKey === 'eccomp') {
        return document.title || document.case_title || document.case_number || this.$t('dossier.docTitles.eccomp', { n: index + 1 });
      }

      if (sourceKey === 'upc') {
        if (document.parties) return document.parties;
        if (document.court || document.date_raw) {
          const date = document.date_raw || document.date || '';
          return `${document.court || ''}${document.court && date ? ' - ' : ''}${date}`.trim();
        }
        return this.$t('dossier.docTitles.upc', { n: index + 1 });
      }

      if (sourceKey === 'edpb') {
        return document.title || document.slug || this.$t('dossier.docTitles.edpb', { n: index + 1 });
      }

      // Handle Mention (documents cités par l'utilisateur)
      if (sourceKey === 'mention') {
        // 1. Si un titre explicite existe déjà
        if (document.title) return document.title;

        // 2. Construction dynamique (Type - Juridiction - Date)
        const parts = [];

        if (document.type) parts.push(document.type.charAt(0).toUpperCase() + document.type.slice(1));

        if (document.jurisdiction) {
           // Éviter la répétition si le type contient déjà la juridiction
           if (!document.type || !document.type.toLowerCase().includes(document.jurisdiction.toLowerCase())) {
               parts.push(document.jurisdiction);
           }
        }

        if (document.date) {
           try {
             const dateObj = new Date(document.date);
             if (!isNaN(dateObj.getTime())) {
               parts.push(this.$localeDate(dateObj));
             } else {
               parts.push(document.date);
             }
           } catch(e) { parts.push(document.date); }
        }

        if (parts.length > 0) return parts.join(' - ');

        return this.$t('dossier.docTitles.documentCited', { n: index + 1 });
      }

      // Default case: use title, textNumber, or fallback
      return document.title || document.textNumber || this.getDefaultDocumentName(sourceKey, index);
    },

    formatActesSocietesDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return this.$localeDate(date, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    // Helper method for statuts titles
    getStatutsTitleForSynthesis(pdfName) {
      if (!pdfName) return '';
      return pdfName.replace(/\.pdf$/i, '');
    },

    // Handle clicks on synthesis references - Opens article detail panel directly
    handleSynthesisReferenceClick(event) {
      event.preventDefault();

      // 1) Citation workspace [doc:N] — ouvre le modal preview PDF
      const wsChip = event.target.closest('.workspace-citation');
      if (wsChip) {
        const nodeId = wsChip.getAttribute('data-workspace-node-id');
        if (nodeId) this.openWorkspaceCitation(nodeId);
        return;
      }

      // 2) Références juridiques standards (Legiarti/BOFIP/…)
      const refElement = event.target.closest('.synthesis-reference');
      if (!refElement) return;

      const sourceKey = refElement.getAttribute('data-source');
      const docIndex = parseInt(refElement.getAttribute('data-index'), 10);
      const pointIndex = parseInt(refElement.getAttribute('data-point-index'), 10);

      if (sourceKey && !isNaN(docIndex) && !isNaN(pointIndex)) {
        // Open article detail panel directly
        this.openArticleDetailPanel(sourceKey, docIndex, pointIndex);
      }
    },

    openWorkspaceCitation(nodeId) {
      const citations = this.workspaceCitationsMap || {};
      const meta = citations[String(nodeId)];
      if (!meta) {
        console.warn('Citation workspace introuvable pour', nodeId, '— map:', Object.keys(citations));
        if (this.$toast) this.$toast.warning('Source introuvable pour cette citation.');
        return;
      }
      this.citationModalData = {
        nodeId: String(nodeId),
        workspaceId: this.dossierData && this.dossierData.workspace_id,
        ...meta,
      };
      this.showCitationModal = true;
    },

    // Open article detail panel with document data
    openArticleDetailPanel(sourceKey, docIndex, pointIndex) {
      const point = this.analysesFiscales[pointIndex];
      if (!point) return;

      const doc = this.getDocumentFromPoint(point, sourceKey, docIndex);
      if (!doc) return;

      // Build articleDetailData using the imported utility function
      this.articleDetailData = buildArticleDetailData(doc, sourceKey, docIndex);

      // --- FIX pour MENTION ---
      // Forcer le mapping manuel car buildArticleDetailData ne connait peut-être pas official_link
      if (sourceKey === 'mention') {
        // Map official_link or link to url
        this.articleDetailData.url = doc.link || doc.official_link;

        // Map content (priority: content from backend, fallback to takeaway/full_text)
        this.articleDetailData.content = doc.content || doc.takeaway || doc.full_text || '';

        // S'assurer que les métadonnées sont bien passées à la racine pour l'affichage
        this.articleDetailData.type = doc.type;
        this.articleDetailData.jurisdiction = doc.jurisdiction;
        this.articleDetailData.date = doc.date;
        this.articleDetailData.original_reference = doc.original_reference;

        // Si le titre n'est pas défini, en créer un propre
        if (!this.articleDetailData.title) {
           this.articleDetailData.title = this.getDocumentTitleForSynthesis(doc, sourceKey, docIndex);
        }
      }
      // -----------------------

      this.showArticleDetailPanel = true;

      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    },

    // Close article detail panel
    openTransformModal(mode) {
      this.transformMode = mode;
      this.showTransformModal = true;
    },

    closeTransformModal() {
      this.showTransformModal = false;
    },

    closeArticleDetailPanel() {
      this.showArticleDetailPanel = false;
      this.articleDetailData = {
        title: '',
        content: '',
        source: '',
        sourceData: null,
      };
      // Restore body scroll
      document.body.style.overflow = '';
    },

    toggleColumn(idx) {
      const i = this.hiddenColumns.indexOf(idx);
      if (i === -1) {
        this.hiddenColumns.push(idx);
      } else {
        this.hiddenColumns.splice(i, 1);
      }
    },

    getTableauCellClass(val) {
      if (!val || val === '—') return {};
      const s = String(val).trim().toLowerCase();
      if (s.startsWith('oui')) return { 'tableau-cell-oui': true };
      if (s.startsWith('non') && !s.includes('non précisé')) return { 'tableau-cell-non': true };
      return {};
    },

    getTableauCellBadge(val) {
      if (!val || val === '—') return null;
      const raw = String(val).trim();
      const normalized = raw.toLowerCase();
      const trailing = /[.\s,;:!?·\-–—]*/;
      if (/^oui\b/i.test(normalized)) {
        return {
          status: 'oui',
          label: 'Oui',
          rest: raw.replace(new RegExp('^oui\\b' + trailing.source, 'i'), '')
        };
      }
      if (/^non\b/i.test(normalized) && !/non précisé/i.test(normalized)) {
        return {
          status: 'non',
          label: 'Non',
          rest: raw.replace(new RegExp('^non\\b' + trailing.source, 'i'), '')
        };
      }
      return null;
    },

    async exportTableauToExcel() {
      if (!this.tableauData || this.tableauData.length === 0) return;
      this.isExportingExcel = true;
      try {
        const XLSX = await import('xlsx');
        const headers = [this.$t('dossier.tableau.decision'), ...this.tableauSubQuestions];
        const rows = this.tableauData.map(row => {
          const cells = [row.titre || ''];
          this.tableauSubQuestions.forEach((_, idx) => {
            cells.push(row.colonnes_json?.['Q' + (idx + 1)] || '—');
          });
          return cells;
        });

        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Largeurs de colonnes
        ws['!cols'] = [
          { wch: 40 },
          ...this.tableauSubQuestions.map(() => ({ wch: 50 }))
        ];

        // Style en-tête (gras + fond vert foncé)
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddr = XLSX.utils.encode_cell({ r: 0, c: col });
          if (!ws[cellAddr]) continue;
          ws[cellAddr].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { wrapText: true, vertical: 'center' }
          };
        }

        // Retour à la ligne pour toutes les cellules
        for (let row = 1; row <= range.e.r; row++) {
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddr = XLSX.utils.encode_cell({ r: row, c: col });
            if (!ws[cellAddr]) continue;
            ws[cellAddr].s = { alignment: { wrapText: true, vertical: 'top' } };
          }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, this.$t('dossier.excelExport.sheetName'));

        const contexte = this.dossierData?.contexte || this.$t('dossier.excelExport.filenameFallback');
        const fileName = `${this.$t('dossier.excelExport.filenamePrefix')}_${contexte.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
        XLSX.writeFile(wb, fileName);
      } catch (e) {
        console.error('Excel export failed:', e);
        this.$toast?.error(this.$t('dossier.toasts.excelError'));
      } finally {
        this.isExportingExcel = false;
      }
    },

    openTableauCellModal(title, text) {
      this.tableauCellModalTitle = title || '';
      this.tableauCellModalText = text || '—';
      this.showTableauCellModal = true;
    },

    openTableauDecisionPanel(row) {
      // Génère un contenu lisible à partir des colonnes_json (fallback si pas de summary)
      const colonnesContent = Object.entries(row.colonnes_json || {})
        .map(([key, val], idx) => {
          const label = this.tableauSubQuestions[idx] || key;
          return `**${label}**\n${val || '—'}`;
        })
        .join('\n\n');

      // Priorité au summary de la décision, sinon les colonnes du tableau
      const mainContent = (row.summary && row.summary.trim()) ? row.summary : colonnesContent;

      this.articleDetailData = {
        title: row.titre || this.$t('dossier.tableau.courtDecision'),
        content: mainContent,
        source: 'juritext',
        id: row.id_juritext || '',
        url: row.lien || '',
        texte: mainContent,
        sourceData: {
          id: row.id_juritext || '',
          title: row.titre || '',
          date: row.date || '',
          summary: row.summary || colonnesContent,
          texte: mainContent,
          lien: row.lien || '',
          official_link: row.lien || '',
          juris_type: row.juridiction || '',
          note: row.note,
        },
      };
      this.showArticleDetailPanel = true;
      document.body.style.overflow = 'hidden';
    },

    autoResize(event) {
      const el = event.target;
      // Reset height so shrinking works too
      el.style.height = 'auto';
      // Set height to scroll height
      el.style.height = `${el.scrollHeight}px`;
    },

    // New method to format search query with line breaks
    formatSearchQuery(text) {
      if (!text) return '';

      // Escape HTML to prevent XSS attacks
      let formattedText = this.escapeHtml(text);

      // Convert \n to <br> tags
      formattedText = formattedText.replace(/\n/g, '<br>');

      return DOMPurify.sanitize(formattedText, { ALLOWED_TAGS: ['br', 'strong', 'em'] });
    },

    checkQueryHeight() {
      this.$nextTick(() => {
        const queryElement = this.$el.querySelector('.search-query');
        if (!queryElement) return;

        const actualHeight = queryElement.scrollHeight;
        const fontSize = parseFloat(window.getComputedStyle(queryElement).fontSize);
        const maxHeight = fontSize * 15; // 15em

        this.isSearchQueryCollapsible = actualHeight > maxHeight;
      });
    },

    toggleSearchQueryExpand() {
      this.isSearchQueryExpanded = !this.isSearchQueryExpanded;
    },

    // Method to scroll to the cadre juridique section
    scrollToCadreJuridique() {
      if (this.$refs.cadreJuridique && this.isAnalysing === 'done') {
        // Get the element and its position
        const element = this.$refs.cadreJuridique;

        // Calculate position to center the element in the viewport
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);

        // Scroll to the element
        window.scrollTo({
          top: middle,
          behavior: 'smooth'
        });
      }
    },

    // Method to scroll to the progress bar section
    scrollToProgressBar() {
      if (this.$refs.progressBarSection && this.isAnalysing === 'pending') {
        // Get the element and its position
        const element = this.$refs.progressBarSection;

        // Calculate position to center the element in the viewport
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);

        // Scroll to the element
        window.scrollTo({
          top: middle,
          behavior: 'smooth'
        });
      }
    },

    // New method to count sources for a point item
    countSources(pointItem) {
      if (!pointItem.analyses) return 0;

      let totalDocuments = 0;

      // Go through each source block in the analyses array
      pointItem.analyses.forEach(sourceBlock => {
        if (sourceBlock.analysis && Array.isArray(sourceBlock.analysis)) {
          totalDocuments += sourceBlock.analysis.length;
        }
      });

      return totalDocuments;
    },

    startPolling() {
      // Clear any existing polling interval
      this.stopPolling();

      // Set up new polling interval
      this.pollingInterval = setInterval(async () => {
        await this.pollAnalysisStatus();
      }, this.pollingFrequency);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    // Poll for status changes
   async pollAnalysisStatus() {
      try {
        // --- CORRECTION 1: Capturer l'état *avant* la requête réseau ---
        const previousStatus = this.isAnalysing;

        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const dossierId = this.$route.params.dossierId;

        // Faire la requête pour vérifier le statut
        const { data: dossierData, error } = await supabase
          .from('dossiers')
          .select('*, MODE') // 'MODE' est bien sélectionné
          .eq('id', dossierId)
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching dossier (polling):', error);
          // Gérer le cas où le dossier est introuvable (par ex. code PGRST116)
          if (error.code === 'PGRST116') {
            this.error = this.$t('dossier.errors.dossierNotFound');
            this.stopPolling(); // Arrêter le polling si le dossier n'existe pas
          }
          return; // Ne pas continuer en cas d'erreur
        }

        // --- CORRECTION 2: Mettre à jour toutes les données en une fois ---
        // Inutile de vérifier data.name etc., car tout est dans dossierData
        this.dossierData = dossierData;
        this.dossierName = dossierData.name || this.$t('dossier.defaults.unnamedDossier');
        this.isAnalysing = dossierData.is_analysing;
        this.dossierMode = dossierData.MODE;

        // ── Agent progress : parser la synthese en cours d'exécution ──
        if (this.dossierMode === 'AGENT' && this.isAnalysing === 'pending' && dossierData.synthese) {
          try {
            const progress = typeof dossierData.synthese === 'string'
              ? JSON.parse(dossierData.synthese)
              : dossierData.synthese;
            if (progress && progress.status === 'running') {
              this.agentProgress = progress;
            }
          } catch (e) {
            // synthese pas encore un JSON valide — ignorer
          }
        }

        // --- La logique de transition fonctionnera maintenant ---
        if (previousStatus === "pending" && this.isAnalysing === "done") {
          // On arrête le polling car l'analyse est terminée
          this.stopPolling();

          setTimeout(async () => {
            // On appelle fetchDossierData pour récupérer les 'analysis_points'
            await this.fetchDossierData();

            if (this.$toast) {
              this.$toast.success(this.$t('dossier.toasts.analysisComplete'));
            }

            // Scroll vers la section des résultats
            this.$nextTick(() => {
              this.scrollToCadreJuridique();
            });
          }, 1500); // Léger délai pour assurer la consistance de la BDD

        } else if (this.isAnalysing === "failed") {
          // Cas où l'analyse a échoué
          console.error('Analysis failed according to DB status.');
          this.stopPolling();
          this.stopProgressBar();
          if (this.$toast) {
            this.$toast.error(this.$t('dossier.toasts.analysisFailed'));
          }
        } else if (previousStatus === "done" && this.isAnalysing === "pending") {
          // Cas où une nouvelle analyse est lancée sur un dossier terminé
          this.$nextTick(() => {
            this.scrollToProgressBar();
          });
        } else if (this.isAnalysing === "done") {
          // Si c'est 'done' et que 'previousStatus' n'était pas 'pending',
          // c'est qu'on pollait sur un dossier déjà terminé. On arrête.
          this.stopPolling();
        }

      } catch (error) {
        console.error('Error polling analysis status:', error);
        // Ne pas arrêter le polling sur une simple erreur réseau,
        // car on veut qu'il réessaie.
      }
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    mapRefTypeToSourceKey(refType) {
        if (refType === 'Jurisprudence') return 'juritext';
        if (refType === 'Legiarti') return 'legiarti';
        if (refType === 'Bofip' || refType === 'BOFiP' || refType === 'BOFIP') return 'bofip';
        if (refType === 'Euritext') return 'euritext';
        if (refType === 'CNN') return 'cnn';
        if (refType === 'BALO') return 'balo';
        if (refType === 'Statuts') return 'statuts';
        if (refType === 'Convbi') return 'convbi';
        if (refType === 'Infofi' || refType === 'Infofin') return 'infofi';
        if (refType === 'ActesSocietes') return 'actes_societes';
        if (['Reglements', 'Reglement', 'Règlements', 'Règlement'].includes(refType)) return 'reglements';
        if (refType === 'BODACC') return 'bodacc';
        if (refType === 'Energy') return 'energy';
        if (refType === 'Concurrence') return 'concurrence';
        if (refType === 'INPI') return 'inpi';
        if (refType === 'Mention') return 'mention';
        if (refType === 'CNIL') return 'cnil';
        if (refType === 'BOSS') return 'boss';
        if (refType === 'ACPR') return 'acpr';
        if (refType === 'AMF') return 'amf';
        if (refType === 'CompCase') return 'eccomp';
        if (refType === 'UPC') return 'upc';
        if (refType === 'EDPB') return 'edpb';
        if (refType === 'AGCM') return 'agcm';
        if (refType === 'GiustiziaAmm') return 'giustizia_amministrativa';
        if (refType === 'EuritextITA') return 'jurisprudence_europe_italy';
        if (refType === 'EurLexITA') return 'eurlex_italy';
        return null;
    },

    goDashboard() {
      this.$router.push({
        name: 'dashboard',
      });
    },

    /**
     * Supprime toutes les références du texte (laisse vide)
     * Utilisé pour les questions de suivi
     */
    removeReferencesFromText(text) {
      if (!text || !this.analysesFiscales) return text;

      // D'abord, expand les références multiples
      const expandedText = this.expandMultiReferences(text);

      // Regex pour capturer toutes les références (avec ou sans index de point)
      const refRegex = new RegExp(`((?:ID:\\s+)?(?:${REF_TYPES})\\s*-\\s*\\d+(?:\\s*-\\s*\\d+)?)`, 'g');

      // Remplacer toutes les références par une chaîne vide
      return expandedText.replace(refRegex, '').replace(/\s{2,}/g, ' ').trim();
    },

    getCleanedSynthesisForFollowUp() {
      if (!this.dossierData || !this.dossierData.synthese) return '';

      // Utiliser la nouvelle méthode qui supprime les références au lieu de les remplacer par des titres
      return this.removeReferencesFromText(this.dossierData.synthese);
    },

    // Updated handleCustomQuestion method
    async handleCustomQuestion() {
      const question = (this.customQuestion || '').trim();
      if (!question) return;

      // Mode AGENT : on enchaîne sur le contexte préservé (chaîne reasoning store=False).
      // Pas de nouveau dossier, pas de retour au dashboard.
      if (this.dossierMode === 'AGENT' && this.dossierData && this.dossierData.id) {
        try {
          this.customQuestion = '';
          this.isAnalysing = 'pending';
          const session = await supabase.auth.getSession();
          const token = session?.data?.session?.access_token;
          await axios.post(
            `${process.env.VUE_APP_API_URL}/copilot/agent/follow-up`,
            { dossierId: this.dossierData.id, question },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          // Le polling existant sur dossiers.synthese rafraîchira l'UI.
        } catch (error) {
          console.error('Error sending agent follow-up:', error);
          this.isAnalysing = 'done'; // débloque l'UI
          const msg = error?.response?.data?.error;
          if (msg) {
            if (this.$toast) this.$toast.error(msg);
            // Fallback : si le backend dit qu'il manque agent_context (vieux dossier),
            // on retombe sur l'ancien comportement (relance via dashboard).
            if (msg.includes('contexte agent persisté')) {
              this.fallbackFollowUpViaDashboard(question);
            }
          }
        }
        return;
      }

      // Modes non-AGENT (FLASH, standard...) : ancien comportement par dashboard
      this.fallbackFollowUpViaDashboard(question);
    },

    fallbackFollowUpViaDashboard(question) {
      try {
        const cleanedSynthese = this.getCleanedSynthesisForFollowUp();
        const formattedQuery = `Contexte:\n\n${cleanedSynthese}\n\nQuestion de suivi:\n\n${question}`;
        localStorage.setItem('pendingSearchQuery', formattedQuery);
        // Relancer le suivi dans le MÊME mode que le dossier d'origine (et pas en agent par défaut)
        localStorage.setItem('pendingSearchMode', this.dossierMode || 'STANDARD');
        if (this.dossierMode === 'AGENT' && this.dossierData && this.dossierData.workspace_id) {
          localStorage.setItem('pendingWorkspaceId', this.dossierData.workspace_id);
        }
        this.$router.push('/');
      } catch (error) {
        console.error('Error navigating to dashboard:', error);
      }
    },

    openSourceModal() {
      if (this.analysesFiscales && this.analysesFiscales.length > 0) {
        // En mode FLASH, il n'y a qu'un seul "point" à l'index 0
        this.selectedPoint = this.analysesFiscales[0];
        this.initialDocToOpen = null; // N'ouvre aucun document spécifique
        this.showAnalysisModal = true;
        history.pushState({ modalOpen: true }, '', '#analysis');
      }
    },

    // Updated handleFollowUpQuestion method
    handleFollowUpQuestion(question) {
      try {
        // Use cleaned synthesis text instead of raw synthesis
        const cleanedSynthese = this.getCleanedSynthesisForFollowUp();

        // Construct the query with cleaned context and question
        const formattedQuery = `Contexte:\n\n${cleanedSynthese}\n\nQuestion de suivi:\n\n${question}`;

        // Save the formatted query to localStorage
        localStorage.setItem('pendingSearchQuery', formattedQuery);
        // Relancer le suivi dans le MÊME mode que le dossier d'origine (et pas en agent par défaut)
        localStorage.setItem('pendingSearchMode', this.dossierMode || 'STANDARD');
        if (this.dossierMode === 'AGENT' && this.dossierData && this.dossierData.workspace_id) {
          localStorage.setItem('pendingWorkspaceId', this.dossierData.workspace_id);
        }

        // Navigate to dashboard
        this.$router.push('/');
      } catch (error) {
        console.error("Error navigating to dashboard:", error);

        // Fallback to copy to clipboard if navigation fails
        navigator.clipboard.writeText(question);
      }
    },

    formatText(text) {
      if (!text) return '';

      // REMOVE OR COMMENT OUT THIS LINE
      // let formattedText = this.escapeHtml(text);

      // The rest of the function assumes `text` is now the input, or you can rename `formattedText` to `text`
      let formattedText = text; // If you remove the line above, just start with the original text.


      // 1) Repérer tout astérisque en début de ligne (après un retour ou le début de texte),
      //    éventuellement précédé d'espaces, suivi d'un ou plusieurs espaces,
      //    puis capturer le contenu de la ligne.
      // 1) Chaque ligne commençant par "* " devient <li>
      //    margin-left = ( ⌊espaces/4⌋ + 1 ) em

      formattedText = formattedText.replace(/\n\s*\*\*\*\s*\n/g, '\n\n');

      formattedText = formattedText.replace(/####\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/###\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/##\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/#\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');

      formattedText = formattedText.replace(
        /(^|\n)( *)\*\s+(.*?)(?=\n|$)/g,
        (match, sep, indent, content) => {
          const level = Math.floor(indent.length / 4) + 1;         // 1, 2, 3, …
          let bullet;
          switch (level) {
            case 1: bullet = '-'; break;
            case 2: bullet = '•'; break;
            case 3: bullet = '◦'; break;
            default: bullet = '▪';                                 // niveaux ≥ 4
          }
          return `${sep}<li style="margin-left:${level}em; margin-top:1em; margin-bottom:1em;">${bullet} ${content}</li>`;
        }
      );

      // 2) Regrouper les <li> consécutifs dans un <ul>
      formattedText = formattedText.replace(
        /(<li[\s\S]*?<\/li>)+/g,
        m => `<ul>${m}</ul>`
      );

      // 3) Fusionner les </ul><ul> adjacents
      formattedText = formattedText.replace(/<\/ul>\s*<ul>/g, '');


      // Convert line breaks to <br> tags
      formattedText = formattedText.replace(/\n/g, '<br>');

      // Convert **text** to <strong>text</strong>
      formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Convert *text* to <em>text</em> (italics)
      // 1. Transformer les lignes qui démarrent par "* " en <li>


      // Convert _text_ to <u>text</u> (underline)
      formattedText = formattedText.replace(/_(.*?)_/g, '<u>$1</u>');

      // Convert ## headings
      // formattedText = formattedText.replace(/###\s+(.*?)(?:\n|$)/g, '<strong>$1</strong>');

      // Convert ## headings


      // Convert # headings

      return formattedText;
    },

    // Escape HTML to prevent XSS attacks
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    },

    // Handle the click on "Analyse complète" button
    handleAnalyseComplete(pointItem) {
      this.selectedPoint = pointItem;
      this.showAnalysisModal = true;
      history.pushState({ modalOpen: true }, '', '#analysis');
    },

    // Close the analysis modal
    closeAnalysisModal() {
      // --- AJOUT ---
      // On vérifie si on doit revenir en arrière dans l'historique
      const shouldGoBack = window.location.hash === '#analysis';

      // On ferme la modale en mettant à jour l'état local
      this.showAnalysisModal = false;
      this.selectedPoint = null;
      this.initialDocToOpen = null;

      // On fait défiler jusqu'à la section de synthèse après la fermeture de la modale
      this.$nextTick(() => {
        this.scrollToCadreJuridique();
      });

      // --- AJOUT ---
      // Si le hash était présent, on déclenche un "retour" pour nettoyer l'historique.
      // Notre écouteur 'popstate' ne rappellera pas cette fonction en boucle car
      // `this.showAnalysisModal` est déjà `false`.
      if (shouldGoBack) {
        history.back();
      }
    },

    // Close the text modal (for potential future implementation)
    closeTextModal() {
      this.showTextModal = false;
    },

    sanitizeHtmlForDocx(htmlString) {
      let cleanedHtml = htmlString;

      // 1. Remove all SVG elements completely. This is the most likely culprit.
      cleanedHtml = cleanedHtml.replace(/<svg.*?<\/svg>/gs, '');

      // 2. Remove script and style tags, just in case they are present.
      cleanedHtml = cleanedHtml.replace(/<script.*?<\/script>/gs, '');
      cleanedHtml = cleanedHtml.replace(/<style.*?<\/style>/gs, '');

      // 3. (Optional but recommended) Remove elements that are interactive or meaningless in a Word document.
      // For example, remove buttons but keep their text content.
      // This regex finds <button...> tags and replaces them with just their inner content.
      cleanedHtml = cleanedHtml.replace(/<button[^>]*>(.*?)<\/button>/gs, '$1');

      // 4. Remove any remaining spinner animations
      cleanedHtml = cleanedHtml.replace(/<div class="spinner.*?<\/div>/gs, '');


      return cleanedHtml;
    },

    // Add these two new helper methods to your component's 'methods' object
    parseInlineFormatting(text) {
      // On isole tout ce qui ressemble à **…**, _…_ ou *…*
      const tokens = text.split(/(\*\*.*?\*\*|_.*?_|\*.*?\*)/g).filter(Boolean);

      return tokens.map(token => {
        // **gras**
        if (/^\*\*(.+)\*\*$/.test(token)) {
          return new TextRun({
            text: token.slice(2, -2),
            bold: true,
            italics: false,
            underline: false,
          });
        }
        // _souligné_
        if (/^_(.+)_$/.test(token)) {
          return new TextRun({
            text: token.slice(1, -1),
            bold: false,
            italics: false,
            underline: true,
          });
        }
        // *italique*
        if (/^\*(.+)\*$/.test(token)) {
          return new TextRun({
            text: token.slice(1, -1),
            bold: false,
            italics: true,
            underline: false,
          });
        }
        // texte “normal” : ON DÉSACTIVE TOUT style
        return new TextRun({
          text: token,
          bold: false,
          italics: false,
          underline: false,
        });
      });
    },

    // /src/components/DossierPage.vue (dans l'objet methods)

    formatTextToDocx(text) {
        if (!text) return [];

        const lines = text.split('\n');
        const docxObjects = [];
        let i = 0;

        while (i < lines.length) {
            const line = lines[i];

            // Détection de tableau Markdown (ligne contenant des pipes |)
            if (line.trim().includes('|') && line.trim().startsWith('|')) {
                const tableResult = this.parseMarkdownTableForDocx(lines, i);
                if (tableResult.table) {
                    docxObjects.push(tableResult.table);
                    i = tableResult.nextIndex;
                    continue;
                }
            }

            // Heading niveau 1
            if (line.startsWith('# ')) {
                const content = line.substring(2);
                docxObjects.push(new Paragraph({
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 240, after: 120 },
                    children: this.parseInlineFormatting(content),
                    alignment: AlignmentType.LEFT,
                }));
                i++;
                continue;
            }
            // Heading niveau 2
            if (line.startsWith('## ')) {
                const content = line.substring(3);
                docxObjects.push(new Paragraph({
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 200, after: 100 },
                    children: this.parseInlineFormatting(content),
                    alignment: AlignmentType.LEFT,
                }));
                i++;
                continue;
            }
            // Heading niveau 3
            if (line.startsWith('### ')) {
                const content = line.substring(4);
                docxObjects.push(new Paragraph({
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 180, after: 80 },
                    children: this.parseInlineFormatting(content),
                    alignment: AlignmentType.LEFT,
                }));
                i++;
                continue;
            }

            // Bullet points
            const bulletMatch = line.match(/^( *)(?:\*|-)\s(.*)/);
            if (bulletMatch) {
                const indent = bulletMatch[1].length;
                const content = bulletMatch[2];
                const level = Math.floor(indent / 4);

                docxObjects.push(new Paragraph({
                    bullet: { level },
                    children: this.parseInlineFormatting(content),
                    alignment: AlignmentType.JUSTIFIED,
                }));
                i++;
                continue;
            }

            // Lignes vides -> paragraphe vide
            if (line.trim() === '') {
                docxObjects.push(new Paragraph(''));
                i++;
                continue;
            }

            // Paragraphe normal
            docxObjects.push(new Paragraph({
              style: "Justified",
              children: this.parseInlineFormatting(line)
            }));
            i++;
        }

        return docxObjects;
    },

    // Nouvelle méthode corrigée (Version DXA - Largeur fixe absolue)
    parseMarkdownTableForDocx(lines, startIndex) {
      const tableLines = [];
      let currentIndex = startIndex;
      // Collecter toutes les lignes du tableau
      while (currentIndex < lines.length && lines[currentIndex].trim().includes('|')) {
        tableLines.push(lines[currentIndex]);
        currentIndex++;
      }
      if (tableLines.length < 2) {
        return { table: null, nextIndex: startIndex + 1 };
      }
      const headerLine = tableLines[0];
      const headerCells = headerLine.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
      const bodyLines = tableLines.slice(2); // On saute la ligne de séparation ---
      const tableRows = [];
      // NOUVEAU : Pas de largeur fixe totale. On utilise pourcentage pour full-width.
      // Header row
      const headerRow = new TableRow({
        children: headerCells.map(cellContent =>
          new TableCell({
            children: [new Paragraph({
              children: this.parseInlineFormatting(cellContent),
              alignment: AlignmentType.CENTER,
            })],
            margins: {
              top: 200, bottom: 200, left: 200, right: 200,  // AUGMENTÉ pour plus d'espace (en twips, 1/20 point)
            },
            shading: {
              fill: "625CFB",
              color: "auto",
            },
            verticalAlign: "center",
          })
        ),
        tableHeader: true,
      });
      tableRows.push(headerRow);
      // Body rows
      bodyLines.forEach((bodyLine, rowIndex) => {
        const cells = bodyLine.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
        // Remplissage des cellules manquantes si nécessaire
        while (cells.length < headerCells.length) {
          cells.push("");
        }
        const row = new TableRow({
          children: cells.map(cellContent =>
            new TableCell({
              children: [new Paragraph({
                children: this.parseInlineFormatting(cellContent),
                alignment: AlignmentType.LEFT,
              })],
              // NOUVEAU : Pas de largeur fixe par colonne. On laisse auto-ajuster.
              // Si besoin d'une min-width, ajoute : width: { size: 10, type: WidthType.PERCENTAGE } par ex. pour 10% min par colonne.
              margins: {
                top: 200, bottom: 200, left: 200, right: 200,  // AUGMENTÉ pour lisibilité
              },
              shading: {
                fill: rowIndex % 2 === 0 ? "FFFFFF" : "f5f5f5",
              },
              verticalAlign: "center",
            })
          ),
        });
        tableRows.push(row);
      });
      // NOUVEAU : Tableau avec largeur fixe en twips (DXA)
      const table = new Table({
        rows: tableRows,
        width: {
          size: 9000,  // ~15.9cm en twips (largeur A4 moins marges)
          type: WidthType.DXA,  // Utilise DXA au lieu de PERCENTAGE pour éviter les problèmes
        },
        layout: "autofit",  // Les colonnes s'adaptent au contenu
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4, color: "bfbfbf" },
          bottom: { style: BorderStyle.SINGLE, size: 4, color: "bfbfbf" },
          left: { style: BorderStyle.SINGLE, size: 4, color: "bfbfbf" },
          right: { style: BorderStyle.SINGLE, size: 4, color: "bfbfbf" },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: "bfbfbf" },
          insideVertical: { style: BorderStyle.SINGLE, size: 4, color: "bfbfbf" },
        },
      });
      return { table, nextIndex: currentIndex };
    },

    // This is your updated downloadWord method
    // Replace your downloadWord method with this final version
    /**
     * Remplace les références par les titres des documents pour l'export
     * Cette méthode est similaire à processSynthesisReferencesInText mais retourne du texte brut
     */
    getDefaultDocumentName(sourceKey, index) {
      const names = {
        'legiarti': this.$t('dossier.defaultDocNames.legiarti'),
        'juritext': this.$t('dossier.defaultDocNames.juritext'),
        'bofip': this.$t('dossier.defaultDocNames.bofip'),
        'euritext': this.$t('dossier.defaultDocNames.euritext'),
        'cnn': this.$t('dossier.defaultDocNames.cnn'),
        'balo': this.$t('dossier.defaultDocNames.balo'),
        'bodacc': this.$t('dossier.defaultDocNames.bodacc'),
        'statuts': this.$t('dossier.defaultDocNames.statuts'),
        'convbi': this.$t('dossier.defaultDocNames.convbi'),
        'infofi': this.$t('dossier.defaultDocNames.infofi'),
        'actes_societes': this.$t('dossier.defaultDocNames.actes_societes'),
        'reglements': this.$t('dossier.defaultDocNames.reglements'),
        'concurrence': this.$t('dossier.defaultDocNames.concurrence'),
        'inpi': this.$t('dossier.defaultDocNames.inpi'),
        'cnil': this.$t('dossier.defaultDocNames.cnil'),
        'boss': this.$t('dossier.defaultDocNames.boss'),
        'acpr': this.$t('dossier.defaultDocNames.acpr'),
        'amf': this.$t('dossier.defaultDocNames.amf'),
        'eccomp': this.$t('dossier.defaultDocNames.eccomp'),
        'upc': this.$t('dossier.defaultDocNames.upc'),
        'edpb': this.$t('dossier.defaultDocNames.edpb'),
      };
      const name = names[sourceKey] || this.$t('dossier.defaultDocNames.document');
      return `${name} #${index + 1}`;
    },

    replaceReferencesWithTitlesForExport(text) {
      if (!text || !this.analysesFiscales) return text;

      const expandedText = this.expandMultiReferences(text);

      // Mode FLASH : références avec crochets optionnels et BOFIP (ex: "[BOFIP - 1]")
      const refRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\s*-\\s*\\d+(?:\\s*-\\s*\\d+)?\\]?)`, 'g');

      return expandedText.replace(refRegex, (match) => {
        let refMatch = match.match(new RegExp(`^\\[?(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)\\s*-\\s*(\\d+)\\]?$`));

        let refType, refIndex, pointIndex;

        if (refMatch) {
          refType = refMatch[1];
          refIndex = parseInt(refMatch[2], 10) - 1;
          pointIndex = parseInt(refMatch[3], 10) - 1;
        } else {
          refMatch = match.match(new RegExp(`^\\[?(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)\\]?$`));

          if (refMatch) {
            refType = refMatch[1];
            refIndex = parseInt(refMatch[2], 10) - 1;
            pointIndex = this.dossierMode === 'FLASH' ? 0 : null;
          } else {
            return match;
          }
        }

        // Map reference types to source keys
        let sourceKey = this.mapRefTypeToSourceKey(refType);
        if (!sourceKey) return match;
        else if (refType === 'Euritext') sourceKey = 'euritext';
        else if (refType === 'CNN') sourceKey = 'cnn';
        else if (refType === 'BALO') sourceKey = 'balo';
        else if (refType === 'Statuts') sourceKey = 'statuts';
        else if (refType === 'Convbi') sourceKey = 'convbi';
        else if (refType === 'Infofi' || refType === 'Infofin') sourceKey = 'infofi';
        else if (refType === 'ActesSocietes') sourceKey = 'actes_societes';
        else if (refType === 'Reglements') sourceKey = 'reglements';
        else if (refType === 'Reglement') sourceKey = 'reglements';
        else if (refType === 'Règlements') sourceKey = 'reglements';
        else if (refType === 'Règlement') sourceKey = 'reglements';
        else if (refType === 'BODACC') sourceKey = 'bodacc';
        else if (refType === 'Energy') sourceKey = 'energy';
        else if (refType === 'Concurrence') sourceKey = 'concurrence';
        else if (refType === 'INPI') sourceKey = 'inpi';
        else if (refType === 'Mention') sourceKey = 'mention';
        else if (refType === 'CNIL') sourceKey = 'cnil';
        else if (refType === 'BOSS') sourceKey = 'boss';
        else if (refType === 'ACPR') sourceKey = 'acpr';
        else if (refType === 'AMF') sourceKey = 'amf';
        else if (refType === 'CompCase') sourceKey = 'eccomp';
        else if (refType === 'UPC') sourceKey = 'upc';
        else if (refType === 'EDPB') sourceKey = 'edpb';

        if (!sourceKey) return match;

        let finalPointIndex = pointIndex;
        let document = null;

        if (pointIndex !== null) {
          // NOUVEAU FORMAT: Utiliser l'index de point fourni
          if (pointIndex < this.analysesFiscales.length) {
            const point = this.analysesFiscales[pointIndex];
            document = this.getDocumentFromPoint(point, sourceKey, refIndex);
            finalPointIndex = pointIndex;
          }
        } else {
          // ANCIEN FORMAT: Rechercher le document dans tous les points
          const pointWithReference = this.findPointContainingReference(sourceKey, refIndex);
          if (pointWithReference) {
            document = this.getDocumentFromPoint(pointWithReference.point, sourceKey, refIndex);
            finalPointIndex = pointWithReference.pointIndex;
          }
        }

        if (document && finalPointIndex !== null) {
          const documentTitle = this.getDocumentTitleForSynthesis(document, sourceKey, refIndex);
          return documentTitle;
        }

        return match; // Garder la référence originale si pas de document trouvé
      });
    },

    /**
     * Extrait les références (titre + lien) d'un texte pour l'export Word.
     * Retourne un tableau d'objets { title, link } dédupliqués.
     */
    collectReferencesForWordExport() {
      if (!this.analysesFiscales || this.analysesFiscales.length === 0) return [];

      const buildLink = getOfficialDecisionLink;
      const refRegex = new RegExp(`((?:ID:\\s+)?(?:${REF_TYPES})\\s*-\\s*\\d+(?:\\s*-\\s*\\d+)?)`, 'g');

      const textsToScan = [];
      if (this.dossierData?.synthese) {
        textsToScan.push(this.expandMultiReferences(this.dossierData.synthese));
      }
      this.analysesFiscales.forEach(point => {
        if (point.point) textsToScan.push(this.expandMultiReferences(point.point));
        if (point.final_answer_formatted) textsToScan.push(this.expandMultiReferences(point.final_answer_formatted));
      });

      const seen = new Map(); // key: title|link -> { title, link }
      const order = [];

      const extractRefInfo = (match) => {
        let refMatch = match.match(new RegExp(`^(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)\\s*-\\s*(\\d+)$`));
        let refType, refIndex, pointIndex;

        if (refMatch) {
          refType = refMatch[1];
          refIndex = parseInt(refMatch[2], 10) - 1;
          pointIndex = parseInt(refMatch[3], 10) - 1;
        } else {
          refMatch = match.match(new RegExp(`^(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)$`));
          if (refMatch) {
            refType = refMatch[1];
            refIndex = parseInt(refMatch[2], 10) - 1;
            pointIndex = this.dossierMode === 'FLASH' ? 0 : null;
          } else return null;
        }

        const sourceKey = this.mapRefTypeToSourceKey(refType);
        if (!sourceKey) return null;

        let document = null;
        if (pointIndex !== null && pointIndex < this.analysesFiscales.length) {
          document = this.getDocumentFromPoint(this.analysesFiscales[pointIndex], sourceKey, refIndex);
        } else {
          const found = this.findPointContainingReference(sourceKey, refIndex);
          if (found) document = this.getDocumentFromPoint(found.point, sourceKey, refIndex);
        }

        if (!document) return null;
        const title = this.getDocumentTitleForSynthesis(document, sourceKey, refIndex);
        // Pour juritext (CE/CAA/TA), reconstruire le lien depuis l'ID pour garantir le format shareFile/{id}
        let link = null;
        if (sourceKey === 'juritext' && document.id) {
          const built = buildLink(document.id, document.title || '');
          link = (built && built !== '#') ? built : (document.official_link || document.link || null);
        } else {
          link = document.official_link || document.link || null;
        }
        return { title, link };
      };

      textsToScan.forEach(text => {
        const matches = text.match(refRegex) || [];
        matches.forEach(match => {
          const info = extractRefInfo(match);
          if (info) {
            const key = `${info.title}|${info.link || ''}`;
            if (!seen.has(key)) {
              seen.set(key, info);
              order.push(info);
            }
          }
        });
      });

      return order;
    },

    /**
     * Construit la section "Sources" en bas du document Word avec les liens cliquables.
     */
    buildReferencesSectionForWord() {
      const references = this.collectReferencesForWordExport();
      if (references.length === 0) return [];

      const elements = [
        new Paragraph(""),
        new Paragraph({
          text: this.$t('dossier.wordExport.sourcesTitle'),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
          alignment: AlignmentType.JUSTIFIED
        }),
      ];

      references.forEach((ref, index) => {
        if (ref.link) {
          elements.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${index + 1}. `, size: 22 }),
                new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: ref.title,
                      size: 22,
                      style: "Hyperlink",
                    })
                  ],
                  link: ref.link
                })
              ],
              spacing: { after: 120 }
            })
          );
        } else {
          elements.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${index + 1}. ${ref.title}`, size: 22 })
              ],
              spacing: { after: 120 }
            })
          );
        }
      });

      return elements;
    },

    /**
     * Nettoie tout le contenu d'un point d'analyse en remplaçant les références par les titres
     */
    cleanAnalysisPointForExport(pointItem) {
      const cleanedPoint = { ...pointItem };

      // Nettoyer le titre du point
      if (cleanedPoint.point) {
        cleanedPoint.point = this.replaceReferencesWithTitlesForExport(cleanedPoint.point);
      }

      // Nettoyer la réponse finale formatée
      if (cleanedPoint.final_answer_formatted) {
        cleanedPoint.final_answer_formatted = this.replaceReferencesWithTitlesForExport(cleanedPoint.final_answer_formatted);
      }

      return cleanedPoint;
    },

    /**
     * Nettoie les données du dossier pour l'export
     */
    cleanDossierDataForExport() {
      const cleanedData = { ...this.dossierData };

      // Nettoyer la synthèse
      if (cleanedData.synthese) {
        cleanedData.synthese = this.replaceReferencesWithTitlesForExport(cleanedData.synthese);
      }

      return cleanedData;
    },

    /**
     * Nettoie les analyses fiscales pour l'export
     */
    cleanAnalysesFiscalesForExport() {
      return this.analysesFiscales.map(pointItem => this.cleanAnalysisPointForExport(pointItem));
    },

    // MÉTHODE MISE À JOUR pour downloadWord
    async downloadWord(shortOnly = false) {
      // Fermer la modal
      this.showWordExportModal = false;

      if (!this.isDataReadyForPDF) {
        return;
      }
      this.isDownloadingWord = true;
      if (this.$toast) {
        const message = shortOnly
          ? this.$t('dossier.toasts.wordShortGenerating')
          : this.$t('dossier.toasts.wordFullGenerating');
        this.$toast.info(message);
      }

      try {
        // Obtenir la date du jour
        const today = new Date();
        const dateStrForFilename = today.toISOString().split('T')[0]; // Format YYYY-MM-DD
        const formattedDateForDoc = this.$localeDate(today, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });

        // Extraire seulement la question de suivi si c'est le cas
        let contexteToExport = this.dossierData.contexte || this.$t('dossier.defaults.dossier');
        if (this.dossierData.contexte &&
            this.dossierData.contexte.includes('Question de suivi:') &&
            this.dossierData.contexte.includes('Contexte:')) {
          const parts = this.dossierData.contexte.split('Question de suivi:');
          if (parts.length > 1) {
            contexteToExport = parts[1].trim();
          }
        }

        // MODIFICATION: Utiliser les données nettoyées
        const cleanedDossierData = this.cleanDossierDataForExport();
        const cleanedAnalysesFiscales = this.cleanAnalysesFiscalesForExport();

        const doc = new Document({
          // Sans ce flag, Word étire sur toute la largeur les lignes justifiées
          // terminées par un saut de ligne manuel (<br> -> TextRun break:1)
          compatibility: { doNotExpandShiftReturn: true },
          styles: {
            default: {
              paragraph: {
                run: {
                  size: 22, // 11pt (22 / 2)
                },
                alignment: AlignmentType.JUSTIFIED,
              },
            },
            paragraphStyles: [{
              id: "Heading1",
              name: "Heading 1",
              run: {
                size: 28,
                bold: true
              }
            }, {
              id: "Heading2",
              name: "Heading 2",
              run: {
                size: 24,
                bold: true
              }
            }, {
              id: "Justified",
              name: "Justified",
              basedOn: "Normal",
              next: "Normal",
              paragraph: {
                alignment: AlignmentType.JUSTIFIED,
                spacing: { before: 0, after: 0, line: 276 }
              }
            }],
          },
          sections: [{
            children: [
              // Titre principal
              new Paragraph({
                children: [
                  new TextRun({
                    text: contexteToExport,
                    size: 32,
                    bold: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),

              // Paragraphe pour la date de génération
              new Paragraph({
                children: [
                  new TextRun({
                    text: this.$t('dossier.wordExport.generatedOn', { date: formattedDateForDoc }),
                    italics: true,
                    size: 20, // 10pt
                  })
                ],
                alignment: AlignmentType.CENTER,
              }),

              new Paragraph(""), // Espace

              // MODIFICATION: Contenu de la synthèse nettoyée
              ...this.formatTextToDocx(cleanedDossierData.synthese),
              new Paragraph(""),

              // Inclure l'analyse détaillée seulement si pas en mode "short only"
              ...(shortOnly ? [] : [
                // Titre de l'analyse détaillée
                new Paragraph({
                  text: this.$t('dossier.wordExport.detailedResearchTitle'),
                  heading: HeadingLevel.HEADING_1,
                  spacing: {
                    before: 300,
                    after: 150,
                  },
                  alignment: AlignmentType.JUSTIFIED
                }),

                // MODIFICATION: Boucle sur les points d'analyse nettoyés
                ...cleanedAnalysesFiscales.flatMap(pointItem => {
                  return [
                    new Paragraph(""),
                    // Titre du point nettoyé
                    new Paragraph({
                      text: pointItem.point,
                      heading: HeadingLevel.HEADING_2,
                      alignment: AlignmentType.JUSTIFIED
                    }),

                    // Contenu de la réponse nettoyée
                    ...this.formatTextToDocx(pointItem.final_answer_formatted),
                  ];
                }),
              ]),

              // Section Sources / Références en bas de page avec les liens cliquables
              ...this.buildReferencesSectionForWord(),
            ],
          }],
        });

        const blob = await Packer.toBlob(doc);
        const filename = `${dateStrForFilename}_${PdfGenerator._sanitizeFilename((this.dossierData.name || this.$t('dossier.defaults.dossier')))}.docx`;
        saveAs(blob, filename);

        if (this.$toast) {
          this.$toast.success(this.$t('dossier.toasts.wordSuccess'));
        }
      } catch (error) {
        console.error("Erreur lors de la génération du document Word:", error);
        if (this.$toast) {
          this.$toast.error(this.$t('dossier.toasts.wordError'));
        }
      } finally {
        this.isDownloadingWord = false;
      }
    },

    // MÉTHODE MISE À JOUR pour downloadPDF
    async downloadPDF() {
      try {
        // Set loading state to true
        this.isDownloadingPDF = true;

        // Show loading notification
        if (this.$toast) {
          this.$toast.info(this.$t('dossier.toasts.pdfPreparing'));
        }

        // Create a promise that rejects after 30 seconds
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error(this.$t('dossier.errors.pdfTimeout')));
          }, 30000); // 30 seconds timeout
        });

        // Create the PDF generation promise
        const pdfPromise = (async () => {
          // MODIFICATION: Utiliser les données nettoyées
          const cleanedDossierData = this.cleanDossierDataForExport();
          const cleanedAnalysesFiscales = this.cleanAnalysesFiscalesForExport();

          // Generate HTML content avec les données nettoyées
          const htmlContent = await PdfGenerator.generateDossierHTML(
            cleanedDossierData,
            cleanedAnalysesFiscales,
            TextFormatter.format
          );

          // Create a temporary div to hold the content
          const element = document.createElement('div');
          element.innerHTML = htmlContent;
          document.body.appendChild(element);

          // Configure PDF options
          const opt = {
            margin: [0.5, 0.5], // inches: [top/bottom, left/right]
            filename: PdfGenerator._sanitizeFilename((this.dossierData.name || this.$t('dossier.defaults.dossier')).replace('Détermine si cette affirmation est vraie ou fausse', '').trim()) + '.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              letterRendering: true,
              allowTaint: false,
              scrollY: 0,
              scrollX: 0,
              windowWidth: 794, // A4 width in pixels at 96 DPI
              windowHeight: 1123 // A4 height in pixels at 96 DPI
            },
            jsPDF: {
              unit: 'in',
              format: 'a4',
              orientation: 'portrait',
              compress: true
            }
          };

          // Generate and download PDF
          await html2pdf().set(opt).from(element).save();

          // Clean up
          document.body.removeChild(element);
        })();

        // Race between the PDF generation and the timeout
        await Promise.race([pdfPromise, timeoutPromise]);

        if (this.$toast) {
          this.$toast.success(this.$t('dossier.toasts.pdfSuccess'));
        }
      } catch (error) {
        console.error("Erreur lors de la génération du PDF:", error);

        if (this.$toast) {
          if (error.message.includes("temps de génération") || error.message.includes("tempo di generazione")) {
            this.$toast.error(this.$t('dossier.toasts.pdfTimeoutError'));
          } else {
            this.$toast.error(this.$t('dossier.toasts.pdfError'));
          }
        }
      } finally {
        // Reset loading state regardless of success or failure
        this.isDownloadingPDF = false;
      }
    },

    // Updated fetchDossierData method
    async fetchDossierData() {
      try {
        this.loading = true;
        const user = await getCurrentUser();
        if (!user) {
          this.error = this.$t('dossier.errors.pleaseConnect');
          this.loading = false;
          return;
        }

        const dossierId = this.$route.params.dossierId;
        if (!dossierId) {
          this.error = this.$t('dossier.errors.missingId');
          this.loading = false;
          return;
        }

        // 1. Fetch dossier metadata (unchanged)
        const { data: dossierData, error } = await supabase
          .from('dossiers')
          .select('*')
          .eq('id', dossierId)
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching dossier:', error);
          this.error = this.$t('dossier.errors.dossierNotFound');
          this.loading = false;
          return;
        }

        this.dossierName = dossierData.name || this.$t('dossier.defaults.unnamedDossier');
        this.isAnalysing = dossierData.is_analysing;
        this.dossierData = dossierData;
        this.dossierMode = dossierData.MODE;

        // ── Agent : extraire la timeline de la synthese terminée ──
        if (this.dossierMode === 'AGENT' && dossierData.is_analysing === 'done' && dossierData.synthese) {
          try {
            const parsed = typeof dossierData.synthese === 'string'
              ? JSON.parse(dossierData.synthese)
              : dossierData.synthese;
            if (parsed && parsed.timeline && Array.isArray(parsed.timeline)) {
              this.agentProgress = { status: 'done', current_iteration: parsed.iterations || 1, steps: parsed.timeline };
            }
          } catch (e) { /* ignore */ }
        }

        // 2. Manage polling (unchanged)
        if (dossierData.is_analysing === 'pending') {
          this.startPolling();
          this.startProgressBar();
          this.startMessageInterval();
          this.analysesFiscales = [];
        } else {
          this.stopPolling();
          if (dossierData.is_analysing === 'done') {
            try {
              // 3. Fetch all analysis points. Use select('*') to get all columns
              // This is necessary to handle both old and new data structures.
              const { data: pointsData, error: pointsError } = await supabase
                .from('analysis_points')
                .select('*') // Fetch all columns to detect the format
                .eq('dossier_id', dossierId)
                .order('point_index');

              if (pointsError) {
                console.error('Error fetching analysis points:', pointsError);
                this.error = this.$t('dossier.errors.analysesFetch');
              } else if (this.dossierMode === 'TABLEAU' && pointsData && pointsData.length > 0) {
                const firstPoint = pointsData[0];
                this.tableauSubQuestions = (firstPoint.point_data && firstPoint.point_data.sub_questions) || [];
                this.tableauData = (firstPoint.juritext_docs || []).map((decision, idx) => ({
                  id_juritext: decision.id || '',
                  titre: decision.title || decision.titre || this.$t('dossier.tableau.decisionNumbered', { n: idx + 1 }),
                  date: decision.date || '',
                  juridiction: decision.juris_type || decision.juridiction || '',
                  lien: decision.lien || '',
                  summary: decision.summary || '',
                  colonnes_json: decision.colonnes_json || {}
                }));
              } else if (pointsData && pointsData.length > 0) {
                // --- START OF BACKWARD COMPATIBILITY LOGIC ---

                const firstPoint = pointsData[0];
                const isOldFormat = firstPoint.point_data && Array.isArray(firstPoint.point_data.analyses);

                if (isOldFormat) {
                  // --- HANDLE OLD FORMAT ---
                  // If `point_data` contains the `analyses` array, it's the old, monolithic structure.
                  this.analysesFiscales = pointsData.map(dbItem => dbItem.point_data);

                } else {
                  // --- HANDLE NEW FORMAT ---
                  // Otherwise, it's the new columnar structure that needs to be reconstructed.
                  this.analysesFiscales = pointsData.map(dbItem => {
                    // Start with the metadata from the `point_data` column
                    const transformedPoint = { ...dbItem.point_data };
                    // Re-create the 'analyses' array that the component expects
                    transformedPoint.analyses = [];

                    const sourceColumnMap = {
                      'legiarti': 'legiarti_docs', 'juritext': 'juritext_docs',
                      'bofip': 'bofip_docs', 'euritext': 'euritext_docs',
                      'cnn': 'cnn_docs', 'balo': 'balo_docs', 'statuts': 'statuts_docs',
                      'convbi': 'convbi_docs', 'infofi': 'infofi_docs', 'reglements': 'reglements_docs',
                      'bodacc': 'bodacc_docs', 'energy': 'energy_docs', 'actes_societes': 'actes_societes_docs',
                      'concurrence': 'concurrence_docs',  'inpi': 'inpi_docs', 'cnil': 'cnil_docs', 'boss': 'boss_docs', 'acpr': 'acpr_docs', 'amf': 'amf_docs', 'eccomp': 'eccomp_docs',
                      'upc': 'upc_docs',
                      'edpb': 'edpb_docs',
                      'agcm': 'agcm_docs',
                      'giustizia_amministrativa': 'giustizia_amministrativa_docs',
                      'jurisprudence_europe_italy': 'jurisprudence_europe_italy_docs',
                      'eurlex_italy': 'eurlex_italy_docs',
                    };

                    // Iterate over the sources and populate the 'analyses' array from the respective columns
                    for (const sourceKey in sourceColumnMap) {
                      const columnName = sourceColumnMap[sourceKey];
                      if (dbItem[columnName] && Array.isArray(dbItem[columnName]) && dbItem[columnName].length > 0) {
                        transformedPoint.analyses.push({
                          source: sourceKey,
                          analysis: dbItem[columnName]
                        });
                      }
                    }

                    // NOUVEAU : Ajouter les documents mentionnés par l'utilisateur
                    if (dbItem.mentioned_docs && Array.isArray(dbItem.mentioned_docs)) {
                      transformedPoint.mentioned_docs = dbItem.mentioned_docs;
                    }

                    return transformedPoint;
                  });
                }
                // --- END OF BACKWARD COMPATIBILITY LOGIC ---

                // Extraire la map des citations workspace (mode AGENT + workspace sélectionné)
                const firstPd = (this.analysesFiscales[0] || {});
                this.workspaceCitationsMap = firstPd.workspace_citations || {};

                // 5. The rest of the component logic now receives a consistently structured `analysesFiscales` object.
                this.dossierData.analyses_fiscales = this.analysesFiscales;
                if (this.dossierMode !== 'FLASH' && this.dossierMode !== 'TABLEAU' && this.dossierMode !== 'AGENT') {
                  this.$nextTick(() => this.scrollToCadreJuridique());
                }

              } else {
                // No analysis points found
                this.analysesFiscales = [];
              }
            } catch (analysisError) {
              console.error('Error processing analysis data:', analysisError);
              this.error = this.$t('dossier.errors.analysesProcessing');
            }
          } else {
            this.analysesFiscales = [];
          }
        }
      } catch (error) {
        console.error('Error in fetchDossierData:', error);
        this.error = this.$t('dossier.errors.dossierFetch');
        this.stopPolling();
      } finally {
        this.loading = false;
      }
    },

    async checkAnalysisStatus() {
      try {
        const user = await getCurrentUser();
        const dossierId = this.$route.params.dossierId;

        const { data, error } = await supabase
          .from('dossiers')
          .select('is_analysing')
          .eq('id', dossierId)
          .eq('user_id', user.id)
          .single();

        if (error) throw error;

        this.isAnalysing = data.is_analysing || null;

      } catch (error) {
        console.error('Error checking analysis status:', error);
        this.isAnalysing = null;
      }
    },

    listenToAnalysisStatus() {
      const dossierId = this.$route.params.dossierId;

      // Create a subscription to the dossiers table
      this.subscription = supabase
        .channel(`dossier-${dossierId}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'dossiers',
          filter: `id=eq.${dossierId}`
        }, async (payload) => {
          // Update the local state when the dossier changes
          const previousStatus = this.isAnalysing;
          this.isAnalysing = payload.new.is_analysing;

          // When status changes from "pending" to "done"
          if (previousStatus === "pending" && this.isAnalysing === "done") {
            // Wait a second before refetching to ensure file is available
            setTimeout(async () => {
              await this.fetchDossierData(); // Refresh data to get analysis results

              if (this.$toast) {
                this.$toast.success(this.$t('dossier.toasts.analysisComplete'));
              }
            }, 1000);
          } else {
            // Update the dossierData object with latest data for other status changes
            this.dossierData = {
              ...this.dossierData,
              ...payload.new
            };
          }
        })
        .subscribe();
    },

    getRandomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    stopMessageInterval() {
      if (this.messageInterval) {
        clearInterval(this.messageInterval);
        this.messageInterval = null;
      }
    },

    startProgressBar() {
        this.progressPercentage = 0;

        // Utiliser created_at du dossier comme timestamp de début
        const analysisStartTime = this.dossierData.created_at
            ? new Date(this.dossierData.created_at).getTime()
            : Date.now(); // Fallback au cas où

        const totalTime = this.dossierMode === 'FLASH' ? 360 : this.dossierMode === 'AGENT' ? 540 : this.dossierMode === 'TABLEAU' ? 180 : 900;

        // Time distribution for each stage (ajusté proportionnellement)
        const scaleFactor = totalTime / 900; // 0.4 pour FLASH, 0.6 pour AGENT, 1 pour les autres modes
        const time_to_50_percent = 200 * scaleFactor;
        const time_to_75_percent = 100 * scaleFactor;
        const time_to_90_percent = 100 * scaleFactor;
        const time_to_95_percent = 200 * scaleFactor;
        const time_to_99_percent = 300 * scaleFactor;

        // Cumulative time thresholds
        const time_at_50_percent = time_to_50_percent;
        const time_at_75_percent = time_at_50_percent + time_to_75_percent;
        const time_at_90_percent = time_at_75_percent + time_to_90_percent;
        const time_at_95_percent = time_at_90_percent + time_to_95_percent;
        const time_at_99_percent = time_at_95_percent + time_to_99_percent;

        if (this.progressInterval) {
            clearInterval(this.progressInterval);
        }

        this.progressInterval = setInterval(() => {
            if (this.isAnalysing !== "pending") {
                this.quickCompleteAndStopProgressBar();
                return;
            }

            // Calculer le temps écoulé depuis created_at
            const elapsed = (Date.now() - analysisStartTime) / 1000;
            let newProgress;

            if (elapsed < time_at_50_percent) {
                const fraction = elapsed / time_to_50_percent;
                newProgress = fraction * 50;
            } else if (elapsed < time_at_75_percent) {
                const fraction = (elapsed - time_at_50_percent) / time_to_75_percent;
                newProgress = 50 + fraction * 25;
            } else if (elapsed < time_at_90_percent) {
                const fraction = (elapsed - time_at_75_percent) / time_to_90_percent;
                newProgress = 75 + fraction * 15;
            } else if (elapsed < time_at_95_percent) {
                const fraction = (elapsed - time_at_90_percent) / time_to_95_percent;
                newProgress = 90 + fraction * 5;
            } else if (elapsed < time_at_99_percent) {
                const fraction = (elapsed - time_at_95_percent) / time_to_99_percent;
                newProgress = 95 + fraction * 4;
            } else {
                newProgress = 99;
            }

            this.progressPercentage = Math.min(newProgress, 99);

            if (elapsed >= totalTime && this.isAnalysing === "pending") {
                this.progressPercentage = 99;
                clearInterval(this.progressInterval);
                this.progressInterval = null;
            }
        }, 50);
    },
    // MODIFIED startMessageInterval
    startMessageInterval() {
      if (!this.randomDocs || this.randomDocs.some(value => value === undefined)) {
        this.randomDocs = [
          this.getRandomInt(85, 125),   // First range: 85-125
          this.getRandomInt(185, 225),  // Second range: 185-225
          this.getRandomInt(385, 425),  // Third range: 385-425
          this.getRandomInt(745, 800),  // Fourth range: 745-800
        ];
      }

      const startTime = Date.now();
      // Same scaling factor as progress bar to keep messages synchronized with Flash mode
      const totalTime = this.dossierMode === 'FLASH' ? 360 : this.dossierMode === 'AGENT' ? 540 : this.dossierMode === 'TABLEAU' ? 180 : 900;
      const scalingFactor = totalTime / 900;

      // MODIFIED: Increased message time thresholds to make messages stay longer
      const orig_msg_t1 = 15;   // Increased from 7 to 15
      const orig_msg_t2 = 45;   // Increased from 22 to 45
      const orig_msg_t3 = 85;   // Increased from 36 to 85
      const orig_msg_t4 = 125;  // Increased from 52 to 125
      const orig_msg_t5 = 185;  // Increased from 76 to 185
      const orig_msg_t6 = 245;  // Increased from 98 to 245
      const orig_msg_t7 = 320;  // Increased from 168 to 320

      // Scaled message time thresholds
      const scaled_msg_t1 = orig_msg_t1 * scalingFactor;
      const scaled_msg_t2 = orig_msg_t2 * scalingFactor;
      const scaled_msg_t3 = orig_msg_t3 * scalingFactor;
      const scaled_msg_t4 = orig_msg_t4 * scalingFactor;
      const scaled_msg_t5 = orig_msg_t5 * scalingFactor;
      const scaled_msg_t6 = orig_msg_t6 * scalingFactor;
      const scaled_msg_t7 = orig_msg_t7 * scalingFactor;

      if (this.messageInterval) {
        clearInterval(this.messageInterval);
        this.messageInterval = null;
      }

      this.messageInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;

        if (this.isAnalysing !== "pending") {
          this.stopMessageInterval();
          return;
        }

        if (elapsed < scaled_msg_t1) {
          this.analysisMessage = this.$t('dossier.progress.starting');
        } else if (elapsed < scaled_msg_t2) {
          this.analysisMessage = this.$t('dossier.progress.readingDocs', { n: this.randomDocs[0] });
        } else if (elapsed < scaled_msg_t3) {
          this.analysisMessage = this.$t('dossier.progress.readingDocs', { n: this.randomDocs[1] });
        } else if (elapsed < scaled_msg_t4) {
          this.analysisMessage = this.$t('dossier.progress.readingDocs', { n: this.randomDocs[2] });
        } else if (elapsed < scaled_msg_t5) {
          this.analysisMessage = this.$t('dossier.progress.readingDocs', { n: this.randomDocs[3] });
        } else if (elapsed < scaled_msg_t6) {
          this.analysisMessage = this.$t('dossier.progress.extracting');
        } else if (elapsed < scaled_msg_t7) {
          this.analysisMessage = this.$t('dossier.progress.generating');
        } else {
          // This message will show from scaled_msg_t7 onwards
          this.analysisMessage = this.$t('dossier.progress.generating');
        }
      }, 1000); // Check every 1 second
    },

    quickCompleteAndStopProgressBar() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
        this.progressPercentage = 100;
        setTimeout(() => {
          this.progressPercentage = 0;
        }, 500);
      }
    },

    stopProgressBar() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    }
  },
};
</script>

<style scoped>
.transform-label {
  font-size: 17px;
  color: #374151;
  margin-bottom: 10px;
  margin-top: 16px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.card-container-fullwidth {
  width: 100%;
  margin-bottom: 16px;
}

.card-flash {
  font-size: 0.9rem;
  background-color: white;
  border-radius: 20px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
  color: #000;
  font-weight: 500;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.card-flash .card-text {
  text-align: center;
  font-size: 1.05em;
  color: #333;
}

.card-flash .card-actions .search-button {
  font-size: 14px;
  padding: 10px 30px;
}

.synthesis-content :deep(.synthesis-reference) {
  color: #625CFB !important;
  font-style: italic !important;
  text-decoration: underline !important;
  cursor: pointer !important;
  transition: color 0.2s ease, background-color 0.2s ease !important;
  padding: 0 2px !important;
  border-radius: 2px !important;
  font-size: 0.9em !important;
}

.synthesis-content :deep(.synthesis-reference:hover) {
  color: #4338ca !important;
  background-color: rgba(99, 102, 241, 0.1) !important;
}

.synthesis-content :deep(.inactive-reference) {
  color: #6b7280 !important;
  font-style: italic !important;
  text-decoration: underline dotted !important;
}

.synthesis-content :deep(.workspace-citation) {
  display: inline !important;
  position: relative !important;
  color: #6c63ff !important;
  font-weight: 600 !important;
  text-decoration: none !important;
  cursor: pointer !important;
  transition: color 0.15s ease, background 0.15s ease !important;
  padding: 0 2px !important;
  border-radius: 3px !important;
}
.synthesis-content :deep(.workspace-citation:hover) {
  color: #5a52d5 !important;
  background: #ede9fe !important;
  text-decoration: none !important;
}
/* Tooltip instantané : nom du fichier + pages au survol */
.synthesis-content :deep(.workspace-citation:hover)::after {
  content: attr(data-ws-tip);
  position: absolute;
  bottom: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 320px;
  padding: 5px 10px;
  background: #1a1a2e;
  color: #fff;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: normal;
  text-align: center;
  z-index: 60;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.synthesis-content :deep(.official-link-icon) {
  margin-left: 4px !important;
  color: #625CFB !important;
  opacity: 0.7 !important;
  transition: opacity 0.2s ease !important;
  vertical-align: middle !important;
  display: inline-block !important;
}

.synthesis-content :deep(.official-link-icon:hover) {
  opacity: 1 !important;
}

/* Additional styling for synthesis content */
.synthesis-content {
  cursor: default;
}

.button-expand-row {
  width: 100%;
  display: flex;
  justify-content: center;
}

.button-expand-container {
  width: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
}

hr {
  border: none;
  border-top: 1px solid #a5a5a5;
  width: 80%;
  margin-top: 0.5em;
}

/* Container around input + send button */
.custom-question-container {
  width: 80%;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Make the input expand to fill available space */
.follow-up-input {
  flex-grow: 1;
  padding: 0.6em 1em;
  font-size: 13px;
  border: 1px solid #6c63ff;
  border-radius: 2em;
  transition: box-shadow 0.2s, background-color 0.2s;
}

.follow-up-input:focus {
  outline: none;
  background-color: #f0f2ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.2);
}

/* New “send” button styling */
.follow-up-send-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.5em 1.2em;
  font-size: 12px;
  border: none;
  border-radius: 999px;
  background-color: #625CFB;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;
}

.follow-up-send-button:hover:not(:disabled) {
  background-color: #4f46e5;
}

.follow-up-send-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Add this style for the search query to properly display line breaks */
/* Container for the query and toggle button */
.search-query-container {
  display: flex;
  align-items: flex-start; /* Align button to the top */
  gap: 10px;
  width: 100%;
  max-width: 100%;
  justify-content: center;
  position: relative;
}

.search-query {
  width: 100%;
  max-width: 900px;
  transition: max-height 0.4s ease-in-out;
  overflow: hidden;
  white-space: normal;
  line-height: 1.4;
  box-sizing: border-box;
  padding-bottom: 5px; /* Add some space at the bottom */
  text-align: center;
}


/* Class for the collapsed state */
.collapsed-query {
  /* Set max-height for ~10 lines. (Assuming 1em font-size and ~1.5 line-height) */
  max-height: 15em;
}
/* Buttons */

/* Styling for the new toggle button */
.toggle-query-button {
  width: 80%;
  background: transparent;
  border: none;
  padding: 0;
  margin-top: 0.25em; /* Adjust vertical alignment with the first line of text */
  cursor: pointer;
  color: #6c63ff;
  flex-shrink: 0; /* Prevent the button from shrinking */
}

.follow-up-questions {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  margin-top: 3rem;
}

.follow-up-button {
  color: #6c63ff;
  padding: 0.4em 2.5em !important;
  margin-left: 0;
  font-size: 12px;
  border: 1px solid #6c63ff !important;
  transition: background-color 0.3s;
  margin-right: 0.5rem;
  width: 80%;
}

.follow-up-button:hover {
  color: white;
  background-color: #6c63ff;
  border-color: #6c63ff;
  box-shadow: 0 4px 10px rgba(108, 99, 255, 0.3);
}

/* New styles for source count badge */
.source-count-badge {
  display: inline-flex;
  align-items: center;
  background-color: #f0f4f8;
  border-radius: 12px;
  padding: 3px 8px;
  margin: 8px 0;
  font-size: 11px;
  color: #4a5568;
  font-weight: 500;
}

.source-count-badge svg {
  margin-right: 4px;
  color: #625CFB;
}

.actions-buttons {
  display: flex;
  gap: 10px;
}

.button-group {
  display: flex;
  gap: 10px;
}

.download-button {
  background-color: #4f46e5;
  color: white;
  font-size: 12px;
  display: flex;
  align-items: center;
  padding: 5px 15px;
  transition: background-color 0.3s;
}

.download-button:hover {
  background-color: #4338ca;
}

.download-button[disabled] {
  background-color: #a5a5a5;
  cursor: not-allowed;
}

.download-button svg {
  margin-right: 6px;
}

/* Base styles */
.content {
  font-family: 'Roboto', sans-serif;
  padding: 20px;
  background-color: #F5F6FA;
  min-height: 100vh;
  width: 100vw;
}

.search-section {
  display: grid;
  place-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
}

.tableau-section-full {
  margin-bottom: 10vh;
}

/* Container widths */
.main-section-bottom {
  width: 90%;
  min-width: 0;
  max-width: 100%;
}

.tableau-section-full .main-section-bottom {
  width: 100%;
  max-width: 100%;
}

.tableau-jurisprudence-wrapper {
  padding: 0 16px 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Tableau plein largeur, pas de bord rond */
.tableau-section-full .main-section-bottom {
  width: 100%;
  max-width: 100%;
}

.tableau-jurisprudence-scroll {
  border-radius: 0;
}

.section-carde {
  width: 90%;
}

/* Headers and section layouts */
.container {
  width: 100%;
  margin: 0;
  padding: 0;
}

/* Default header layout (FLASH / AGENT / Normal modes): two-rows */
.search-header { width: 100%; }

.search-header-row--top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.search-header-row--top .search-header-right .actions-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.search-header-row--bottom {
  display: flex;
  justify-content: center;
}

.search-header:not(.search-header--tableau) .search-query {
  margin-top: 2em;
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 768px) {
  .search-header-row--top { align-items: flex-start; }
  .search-header-row--top .search-header-right .actions-buttons { justify-content: flex-end; }
}

/* TABLEAU mode override: single-row layout (back / centered query / actions) */
.search-header--tableau {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: nowrap;
}

.search-header--tableau .search-header-left,
.search-header--tableau .search-header-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.search-header--tableau .search-header-title {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
}

.search-header--tableau .search-query {
  margin: 0;
  width: 100%;
  max-width: 720px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-header--tableau .search-header-right .actions-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
}

.search-header--tableau .button.search-button {
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

@media (max-width: 1100px) {
  .search-header--tableau {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .search-header--tableau .search-header-right {
    width: 100%;
    justify-content: flex-start;
    margin-top: 12px;
  }

  .search-header--tableau .search-header-title {
    order: 3;
    width: 100%;
    justify-content: center;
    margin-top: 12px;
  }

  .search-header--tableau .search-query {
    white-space: normal;
  }
}

.search-title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-title {
  margin: 0;
  margin-right: 8px;
}

.search-timestamp {
  font-size: 12px;
  color: #939393;
}

.edit-icon {
  width: 12px;
  height: 12px;
  margin-bottom: 4px;
}

/* Section styles */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.section-title {
  font-weight: 700;
  text-align: left;
}

.section-content {
  margin-top: 24px;
}

#section-content-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Card sections */
.mes-epingles {
  max-width: 90vw;
  background-color: #FFFFFF;
  padding: 20px;
  border-radius: 8px;
  border: 0.5px solid #BBBBBB;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.1);
  margin-top: 16px;
}

.researchs {
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.1);
  padding: 20px 50px 25px;
  border-radius: 8px;
  margin-top: 32px;
}

/* Card grids */
.card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 24px;
  width: 100%;
}

.card-small-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 24px;
  width: 100%;
}

/* Cards */
.card {
  font-size: 0.8rem;
  background-color: white;
  border-radius: 20px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
  color: #000;
  font-weight: 500;
  padding: 16px;
}

.card-container {
  margin-bottom: 16px;
}

.card-text {
  text-align: justify;
  margin-top: 8px;
}

.download-button svg {
  margin-right: 6px;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Bouton mémo dans le header */
.memo-header-btn {
  background-color: #2563eb !important;
  color: white !important;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
}

.memo-header-btn:hover:not(:disabled) {
  background-color: #1d4ed8 !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.5);
}

.memo-header-btn[disabled] {
  background-color: #94a3b8 !important;
  box-shadow: none;
  cursor: not-allowed;
}

/* Boutons transformation (mémo / e-mail) */
.transform-buttons-row {
  display: flex;
  gap: 12px;
  margin: 16px 0;
  flex-wrap: wrap;
}

.transform-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.transform-action-btn.memo-btn {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #625CFB;
}

.transform-action-btn.memo-btn:hover {
  background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
  box-shadow: 0 2px 8px rgba(98, 92, 251, 0.25);
}

.transform-action-btn.email-btn {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #625CFB;
}

.transform-action-btn.email-btn:hover {
  background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
  box-shadow: 0 2px 8px rgba(98, 92, 251, 0.25);
}

.card-actions {
  display: flex;
  margin-top: 10px;
}

.card-bookmark {
  text-align: right;
}

/* Text styles */
.legal-text {
  text-align: justify;  /* Changed from 'left' to 'justify' for right-justified text */
  font-size: 16px;      /* Changed from 12px to 14px */
  line-height: 1.5;
  width: 80%;           /* Added width of 80% */
  margin-left: auto;    /* Added to center the element */
  margin-right: auto;   /* Added to center the element */
}

/* Preserved existing styling for child elements */
.legal-text h2 {
  font-size: 19px;
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
}

.legal-text h3 {
  font-size: 17px;
  margin-top: 12px;
  margin-bottom: 6px;
  font-weight: 600;
}

.legal-text br {
  display: block;
  margin-top: 8px;
  content: "";
}

/* Buttons */
.button {
  padding: 8px 25px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.search-button {
  background-color: #6c63ff;
  color: white;
  font-size: 12px;
}

.search-button:hover {
  background-color: #5a54d1;
}

.back-button {
  background-color: transparent !important;
  color: #6c63ff !important;
  border: 1.5px solid #6c63ff !important;
  font-size: 12px;
  font-weight: 500;
  border-radius: 999px;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: rgba(108, 99, 255, 0.08) !important;
  border-color: #5a54d1 !important;
  color: #5a54d1 !important;
}

.back-button svg {
  margin-right: 8px;
  margin-top: 0;
  vertical-align: middle;
}

/* Style spécifique pour le bouton Word - plus visible */
.word-button {
  background-color: #2563eb !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
  font-weight: 600;
}

.word-button:hover {
  background-color: #1d4ed8 !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.5);
}

.word-button[disabled] {
  background-color: #94a3b8 !important;
  box-shadow: none;
}

.excel-button {
  background-color: #16a34a !important;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.4);
  font-weight: 600;
}

.excel-button:hover:not([disabled]) {
  background-color: #15803d !important;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.5);
}

.excel-button[disabled] {
  background-color: #94a3b8 !important;
  box-shadow: none;
}

.transparent-button {
  background: transparent;
  padding: 0;
}

.drag-button:focus {
  outline: none;
  box-shadow: none;
}

/* Icons */
.search-icon, .arrow-icon {
  margin-left: 8px;
  width: 16px;
  height: 16px;
}

.action-icons {
  text-align: left;
}

.action-icon {
  width: 12px;
  height: 12px;
  margin-right: 8px;
}

.bookmark-icon {
  width: 12px;
}

/* Media queries for responsiveness */
@media (max-width: 768px) {
  .search-header {
    grid-template-columns: 1fr;
  }

  .search-header-right {
    justify-content: flex-start;
    margin-top: 16px;
  }

  .card-grid {
    grid-template-columns: 1fr;
  }

  .card-small-grid {
    grid-template-columns: 1fr 1fr;
  }

  .main-section-bottom, .section-carde {
    width: 95%;
  }
}

/* Show more/less functionality styles */
.section-content-container {
  position: relative;
  overflow: hidden;
  transition: max-height 0.5s ease;
}

.section-content-container.collapsed {
  max-height: 150px; /* This represents approximately 30% of the content */
}

.fade-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  pointer-events: none;
}

.show-more-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.show-more-button {
  background-color: #f0f0f0;
  color: #6c63ff;
  font-size: 12px;
  border: 1px solid #e0e0e0;
  transition: background-color 0.3s;
}

.show-more-button:hover {
  background-color: #e6e6e6;
}

/* Styles for progress bar and loading message */
.progress-bar-container {
  margin-top: 3em;
  width: 70%;
  margin-left: auto;
  margin-right: auto;
}

.progress {
  height: 20px;
  background-color: lightgrey;
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #6c63ff;
  color: white;
  text-align: center;
  line-height: 20px;
  transition: width 0.6s ease;
}

.progress-bar-striped {
  background-image: linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent);
  background-size: 1rem 1rem;
}

.progress-bar-animated {
  animation: progress-bar-stripes 1s linear infinite;
}

@keyframes progress-bar-stripes {
  from { background-position: 1rem 0; }
  to { background-position: 0 0; }
}

.loading-message {
  color: transparent;
  background: linear-gradient(to left, lightgray 50%, black 50%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% 100%;
  background-position: 100%;
  animation: slide 6s linear infinite;
  cursor: default;
  pointer-events: none;
}

@keyframes slide {
  to {
    background-position: -100%;
  }
}

.title-points {
  margin-bottom: 30px;
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.analysis-message {
  font-size: 1.1em;
  text-align: center;
  margin: 15px 0;
}

/* ========================================= */
/* CORRECTIF TABLEAUX MARKDOWN (v-html)      */
/* ========================================= */

/* On utilise :deep() car le tableau est injecté via v-html */
:deep(.table-wrapper) {
  width: 100%;
  overflow-x: auto;
  margin: 20px 0;
  border: 1px solid #dfe2e5;
  border-radius: 8px;
}

:deep(.markdown-table) {
  width: 100%;
  border-collapse: collapse; /* Indispensable */
  font-size: 14px;
  background-color: white;
}

/* En-tête */
:deep(.markdown-table th) {
  background: linear-gradient(135deg, #625CFB, #7c79ff);
  color: white;
  padding: 12px 15px;
  font-weight: 600;
  text-align: left;
  /* On force l'affichage de la bordure */
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-bottom: 2px solid #5a54d1 !important;
}

/* Cellules standards */
:deep(.markdown-table td) {
  padding: 12px 15px;
  color: #333;
  line-height: 1.5;
  vertical-align: top;

  /* C'EST ICI LA CORRECTION CRUCIALE : On force la main au navigateur */
  border-width: 1px !important;
  border-style: solid !important;
  border-color: #dfe2e5 !important;
}

/* Styles pour les lignes (optionnel mais joli) */
:deep(.markdown-table tbody tr:nth-child(even)) {
  background-color: #f8f9fa;
}

:deep(.markdown-table tbody tr:hover) {
  background-color: #f0f2ff;
}

/* Mise en gras spécifique dans les cellules */
:deep(.markdown-table td strong) {
  color: #625CFB;
  font-weight: 600;
}

/* Bouton Sources pour mode FLASH */
.flash-sources-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
}

.flash-sources-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  font-size: 11px;
  border: 1px solid #625CFB;
  border-radius: 999px;
  background-color: transparent;
  color: #625CFB;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.flash-sources-button:hover {
  background-color: #625CFB;
  color: white;
  box-shadow: 0 2px 8px rgba(98, 92, 251, 0.3);
}

.flash-sources-button svg {
  width: 14px;
  height: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  :deep(.markdown-table th),
  :deep(.markdown-table td) {
    padding: 8px 10px;
    font-size: 13px;
  }
}

/* ========================================= */
/* DROPDOWN EXPORT WORD                      */
/* ========================================= */

.word-export-container {
  position: relative;
}

.word-export-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  overflow: hidden;
  animation: dropdownSlide 0.2s ease;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  font-weight: 500;
}

.dropdown-option:hover {
  background-color: #f3f4f6;
  color: #625CFB;
}

.dropdown-option:active {
  background-color: #e5e7eb;
}

.dropdown-option svg {
  flex-shrink: 0;
  color: #625CFB;
}

.dropdown-option:not(:last-child) {
  border-bottom: 1px solid #f3f4f6;
}

@media (max-width: 640px) {
  .word-export-dropdown {
    min-width: 180px;
  }

  .dropdown-option {
    padding: 10px 14px;
    font-size: 12px;
  }
}

.tableau-section-full .main-section-bottom {
  width: 100%;
  max-width: 100%;
}

.tableau-jurisprudence-scroll {
  overflow-x: auto;
  border-radius: 18px;
  background: white;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.tableau-jurisprudence {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.tableau-jurisprudence thead {
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 3;
}

.tableau-jurisprudence th {
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  /* border-right: 1px solid #e5e7eb; */
  font-size: 13px;
  background: #f8f9fa;
}

.tableau-jurisprudence th:last-child {
  border-right: none;
}

.tableau-th-decision {
  width: 220px;
  min-width: 220px;
  word-wrap: break-word;
  white-space: normal;
}

.tableau-th-column {
  width: 180px;
  min-width: 180px;
  word-wrap: break-word;
  white-space: normal;
  font-weight: 500 !important;
}

.tableau-jurisprudence td {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  /* border-right: 1px solid #e5e7eb; */
  vertical-align: top;
  color: #333;
  line-height: 1.6;
  font-size: 13px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.tableau-jurisprudence td:last-child {
  border-right: none;
}

/* .tableau-jurisprudence tbody tr:nth-child(even) {
  background-color: #f8f9fa;
} */

.tableau-th-decision,
.tableau-td-decision {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
}

.tableau-jurisprudence tbody tr {
  transition: background-color 0.12s ease;
}

.tableau-jurisprudence tbody tr:hover {
  background-color: #faf9ff;
}

.tableau-jurisprudence tbody tr:hover .tableau-td-decision {
  background-color: #faf9ff;
}

.tableau-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px;
  padding: 18px 24px;
  margin-bottom: 16px;
  background: #f8f9fa;
  border-radius: 18px;
  box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06);
}

.tableau-filter-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tableau-select-item {
  flex: 0 1 50px;
}

.tableau-filters-left,
.tableau-filters-right {
  display: none;
}

.tableau-filter-group,
.tableau-toggle-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.tableau-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  line-height: 1;
}

.tableau-checkbox input[type='checkbox'] {
  width: 16px;
  height: 16px;
  margin: 0;
  flex-shrink: 0;
  accent-color: #625cfb;
  cursor: pointer;
  vertical-align: middle;
}

.tableau-checkbox span {
  line-height: 1.25;
  display: inline-flex;
  align-items: center;
}

.tableau-toggle-group {
  gap: 8px;
}

.tableau-toggle {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.tableau-toggle--active {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #4338ca;
}

.tableau-select-group {
  justify-content: flex-end;
}

.tableau-select-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 240px;
  max-width: 280px;
  flex: 1 1 240px;
}

.tableau-select-field label {
  font-size: 11px;
  color: #6b7280;
}

.tableau-select-field select {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background-color: #ffffff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236b7280' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 18px center;
  background-size: 16px;
  padding: 10px 44px 10px 14px;
  color: #111827;
  font-size: 13px;
  line-height: 1.35;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.tableau-select-field select::-ms-expand {
  display: none;
}

.tableau-select-field select:focus {
  outline: none;
  border-color: #a5b4fc;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.tableau-select-field--calendar select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236b7280' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1   .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 18px center;
  background-size: 16px;
}

.tableau-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px 24px;
  width: 100%;
  margin: 0 auto;
  margin-top: 10vh;
}

.tableau-pagination-count-btn {
  margin: 0 5vw;
}

.tableau-pagination-summary {
  font-size: 12px;
  color: #6b7280;
  margin-right: auto;
}

.tableau-pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 100%;
}

.tableau-page-btn {
  width: 34px;
  height: 34px;
  background: transparent;
  color: gray;
  padding: 0;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tableau-page-btn:hover:not(:disabled):not(.tableau-page-btn--active) {
  background: #f3f4f6;
  border-radius: 10px;
  color: #1f2937;
}

.tableau-page-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.tableau-page-btn-action {
  background: #f3f4f6;
  border-radius: 10px;
  border: 1px solid transparent;
}

.tableau-page-btn-action:hover:not(:disabled) {
  background: #e5e7eb;
  color: #1f2937;
}

.tableau-page-btn--active {
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-weight: 700;
  color: #000;
}

.tableau-page-btn--nav {
  width: 34px;
  padding: 0;
}

.tableau-cell-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
  margin-right: 8px;
  white-space: nowrap;
}

.tableau-cell-chip--oui {
  background: #dcfce7;
  color: #166534;
}

.tableau-cell-chip--non {
  background: #fee2e2;
  color: #b91c1c;
}

.tableau-cell-content.tableau-cell-clickable {
  cursor: pointer;
  border-radius: 4px;
  padding: 4px 6px;
  margin: -4px -6px;
  display: flex;
  flex-direction: column;
  transition: background-color 0.15s, box-shadow 0.15s;
}

.tableau-cell-content.tableau-cell-clickable:hover {
  background-color: #f8f9fa;
  box-shadow: 0 1px 4px rgba(108, 99, 255, 0.12);
}

@media (max-width: 900px) {
  .tableau-filters {
    padding: 16px;
  }

  .tableau-filters,
  .tableau-pagination {
    flex-direction: column;
    align-items: stretch;
  }

  .tableau-select-group,
  .tableau-toggle-group,
  .tableau-filter-group {
    justify-content: stretch;
  }

  .tableau-select-field {
    min-width: unset;
    width: 100%;
  }

  .tableau-pagination {
    padding: 16px;
  }

  .tableau-pagination-controls {
    flex-wrap: nowrap;
    overflow-x: auto;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
  }

  .tableau-pagination-controls::-webkit-scrollbar {
    height: 4px;
  }

  .tableau-pagination-controls::-webkit-scrollbar-track {
    background: transparent;
  }

  .tableau-pagination-controls::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 2px;
  }

  .tableau-jurisprudence {
    table-layout: auto;
    min-width: 100%;
  }

  .tableau-jurisprudence th,
  .tableau-jurisprudence td {
    white-space: normal;
    min-width: 160px;
  }

  .tableau-th-decision,
  .tableau-th-column,
  .tableau-td-decision,
  .tableau-td-column {
    position: static !important;
    left: auto !important;
    z-index: auto !important;
    background: transparent !important;
    width: auto !important;
    min-width: 120px !important;
    max-width: none !important;
  }
}

@media (max-width: 640px) {
  .tableau-jurisprudence th,
  .tableau-jurisprudence td {
    padding: 12px 10px;
  }

  .tableau-filters {
    gap: 12px;
    padding: 14px;
  }

  .tableau-filter-group,
  .tableau-toggle-group,
  .tableau-select-group,
  .tableau-pagination-controls {
    gap: 8px;
  }

  .tableau-page-btn {
    width: 30px;
    height: 30px;
    font-size: 12px;
    gap: 0;
  }

  .tableau-page-btn--nav {
    padding: 4px;
  }

  .tableau-pagination {
    padding: 12px 16px;
    margin-top: 5vh;
  }

  .tableau-pagination-controls {
    overflow-x: auto;
    flex-wrap: nowrap;
    justify-content: flex-start;
    padding: 4px 0;
    -webkit-overflow-scrolling: touch;
  }
}

/* .tableau-jurisprudence tbody tr:hover {
  background-color: inherit;
} */

.tableau-td-decision {
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

.tableau-td-column {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

/* Cellules cliquables avec ellipsis si trop long */
.tableau-cell-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  overflow: hidden;
  min-height: 40px;
  max-height: 72px;
}

.tableau-cell-text {
  display: -webkit-box;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  color: #111827;
  font-size: 13px;
  line-height: 1.5;
}

.tableau-cell-clickable {
  cursor: pointer;
  border-radius: 4px;
  padding: 4px 6px;
  margin: -4px -6px;
  transition: background-color 0.15s, box-shadow 0.15s;
}

.tableau-cell-clickable:hover {
  background-color: #ede9fe;
  box-shadow: 0 1px 4px rgba(108, 99, 255, 0.12);
}

/* Oui/Non : vert pour Oui, rouge pour Non */
.tableau-cell-oui {
  color: #166534;
  font-weight: 600;
}

.tableau-cell-non {
  color: #b91c1c;
  font-weight: 600;
}

/* Petit modal pour le texte complet */
.tableau-cell-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.tableau-cell-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 420px;
  max-height: 60vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tableau-cell-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.tableau-cell-modal-title {
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 12px;
}

.tableau-cell-modal-close {
  background: none;
  border: none;
  font-size: 22px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  flex-shrink: 0;
}

.tableau-cell-modal-close:hover {
  color: #4b5563;
}

.tableau-cell-modal-body {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.tableau-cell-modal-text {
  margin: 0;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #374151;
}

.tableau-decision-cell {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  min-width: 0;
  width: 100%;
}

.tableau-decision-cell strong {
  font-size: 13px;
}

/* Même violet que les liens cliquables en mode normal */
.tableau-decision-title-link {
  /* color: #625CFB; */
  cursor: pointer;
  font-weight: 600;
  transition: color 0.15s, text-decoration 0.15s;
}

.tableau-decision-title-link:hover {
  color: #5046e5;
  text-decoration: underline;
}

.tableau-decision-date,
.tableau-decision-juridiction {
  font-size: 11px;
  color: #9ca3af;
}

.tableau-td-decision .tableau-decision-title-link {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tableau-td-decision .tableau-decision-date {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.tableau-empty {
  padding: 40px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: white;
  border-radius: 12px;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.05),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

/* ══════════════════════════════════════════════
   AGENT PROGRESS TIMELINE — Redesign
   ══════════════════════════════════════════════ */

.agent-progress-container {
  max-width: 700px;
  margin: 0 auto;
  padding: 24px;
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  border-radius: 16px;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.01), 0 4px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(108, 99, 255, 0.12);
}

.agent-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.agent-progress-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 10px;
}

.agent-starting-message {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 700px;
  margin: 32px auto;
  padding: 0 24px;
  font-size: 14px;
  color: #6b7280;
  font-style: italic;
}

.agent-starting-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #6c63ff;
  animation: agentDotPulse 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.agent-title-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #6c63ff;
  animation: agentDotPulse 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes agentDotPulse {
  0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(108, 99, 255, 0.4); }
  50% { opacity: 0.7; transform: scale(1.3); box-shadow: 0 0 0 6px rgba(108, 99, 255, 0); }
}

.agent-progress-meta {
  font-size: 12px;
  color: #6c63ff;
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.08), rgba(108, 99, 255, 0.12));
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 500;
  border: 1px solid rgba(108, 99, 255, 0.15);
  font-variant-numeric: tabular-nums;
}

/* Timeline rail */
.agent-timeline {
  position: relative;
  padding-left: 0;
}

.agent-step {
  display: flex;
  gap: 14px;
  padding: 10px 0;
  border-left: 2px solid #e0e4e8;
  margin-left: 14px;
  padding-left: 18px;
  position: relative;
  animation: fadeSlideIn 0.4s ease-out;
}

.agent-step:last-child {
  border-left-color: transparent;
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
}

/* ── Icon bubbles ── */
.step-icon-col {
  flex-shrink: 0;
  width: 28px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  left: -29px;
  margin-right: -14px;
}

.step-icon-bubble {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.step-icon-bubble.reasoning {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.3);
}

.step-icon-bubble.search {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.step-icon-bubble.doc {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.step-icon-bubble.success {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.step-icon-bubble.writing {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.3);
}

/* Pulse animation pour les étapes en cours */
.step-icon-bubble.icon-pulse {
  animation: iconPulse 1.5s ease-in-out infinite;
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(108, 99, 255, 0.4); }
  50% { transform: scale(1.12); box-shadow: 0 0 0 8px rgba(108, 99, 255, 0); }
}

.step-icon-bubble.search.icon-pulse {
  animation: iconPulseBlue 1.5s ease-in-out infinite;
}

@keyframes iconPulseBlue {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
  50% { transform: scale(1.12); box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
}

.step-icon-bubble.doc.icon-pulse {
  animation: iconPulsePurple 1.5s ease-in-out infinite;
}

@keyframes iconPulsePurple {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { transform: scale(1.12); box-shadow: 0 0 0 8px rgba(139, 92, 246, 0); }
}

/* ── Step content ── */
.step-content-col {
  flex: 1;
  min-width: 0;
}

.step-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.step-label-success {
  color: #059669;
}

.step-iter {
  font-weight: 400;
  color: #9ca3af;
  font-size: 11px;
  background: rgba(0, 0, 0, 0.04);
  padding: 1px 8px;
  border-radius: 8px;
}

.step-detail {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.6;
  word-break: break-word;
}

.step-sources {
  font-style: italic;
  color: #8b5cf6;
}

.step-error {
  color: #dc2626;
}

/* ── Texte animé (style dashboard loading-message) ── */
.step-loading-text-animated {
  color: transparent;
  background: linear-gradient(90deg, #6c63ff 50%, #c4c1ff 50%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% 100%;
  background-position: 100%;
  animation: slideTextColor 2s linear infinite;
  font-weight: 500;
  font-size: 12px;
}

@keyframes slideTextColor {
  to { background-position: -100%; }
}

/* ══════════════════════════════════════════════
   AGENT DONE TIMELINE (collapsible)
   ══════════════════════════════════════════════ */

.agent-done-timeline-section {
  margin-bottom: 0;
}

.agent-done-timeline {
  max-width: 700px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  border: 0.5px solid #e0e4e8;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.02), 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 14px 20px;
  user-select: none;
  transition: background-color 0.2s;
}

.agent-done-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 6px 0;
  user-select: none;
}

.agent-done-header:hover {
  background-color: #f8f9fa;
}

.agent-done-header-left {
  display: flex;
  align-items: center;
  color: #374151;
  gap: 2px;
}

.agent-done-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.agent-done-meta {
  font-size: 11px;
  color: #6c63ff;
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.08), rgba(108, 99, 255, 0.12));
  padding: 3px 10px;
  border-radius: 10px;
  font-weight: 500;
  border: 1px solid rgba(108, 99, 255, 0.15);
  font-variant-numeric: tabular-nums;
}

/* Timeline inside the collapsed card */
.agent-done-timeline .agent-timeline {
  padding: 0 20px 16px 20px;
}

.agent-done-timeline .agent-step {
  border-left-color: #e8e6ff;
}

.agent-done-timeline .agent-step:last-child {
  border-left-color: transparent;
}

/* ══════════════════════════════════════════════
   AGENT COST BREAKDOWN (collapsible)
   ══════════════════════════════════════════════ */

.agent-cost-section {
  margin-bottom: 0;
  margin-top: 8px;
}

.agent-cost-card {
  max-width: 700px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  border: 0.5px solid #e0e4e8;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.02), 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 14px 20px;
}

.agent-cost-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 6px 0;
  user-select: none;
}

.agent-cost-header:hover {
  background-color: #f8f9fa;
}

.agent-cost-header-left {
  display: flex;
  align-items: center;
  color: #374151;
  gap: 2px;
}

.agent-cost-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.agent-cost-meta {
  font-size: 11px;
  color: #16a34a;
  background: linear-gradient(135deg, rgba(22, 163, 74, 0.08), rgba(22, 163, 74, 0.12));
  padding: 3px 10px;
  border-radius: 10px;
  font-weight: 500;
  border: 1px solid rgba(22, 163, 74, 0.18);
  font-variant-numeric: tabular-nums;
}

.agent-cost-meta strong {
  font-weight: 700;
}

.agent-cost-body {
  padding: 12px 0 4px 0;
}

.agent-cost-loading,
.agent-cost-error {
  font-size: 12px;
  color: #6b7280;
  padding: 8px 0;
}

.agent-cost-error {
  color: #b91c1c;
}

.agent-cost-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 600px) {
  .agent-cost-grid {
    grid-template-columns: 1fr;
  }
}

.agent-cost-grid-section h6 {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 6px 0;
}

.agent-cost-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.agent-cost-table td {
  padding: 4px 6px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.agent-cost-table td:first-child {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 11px;
  color: #4b5563;
}

.agent-cost-num {
  text-align: right;
  white-space: nowrap;
}
</style>
